// src/pages/Hr/AbsenceCalendarPage.jsx
// =============================================================================
// HR Absence Calendar / Employee Timeline Matrix
// =============================================================================
//
// PURPOSE
// -------
// This page provides the professional absence-calendar view for hb_visits.  It
// renders a monthly employee timeline matrix:
//
//   - horizontal axis: days in the selected period,
//   - vertical axis: employees,
//   - long absences: Gantt-like bars anchored to day columns,
//   - short hourly events: chips inside the exact day cell,
//   - weekends and today: visually highlighted,
//   - leave families: stable, muted category colors,
//   - status: solid / dashed visual language,
//   - details: right-side panel with full event information.
//
// IMPORTANT BUSINESS RULE
// -----------------------
// The calendar is an effective planning view.  It intentionally does not show
// storno/cancellation requests or leave requests already cancelled by a storno
// workflow.  Those objects remain important audit data and are still available
// in Work Items / technical history, but they would make the planning matrix
// misleading.  The user wants to see what is effectively planned.
//
// LAYOUT MODEL
// ------------
// Each employee row is no longer a single flat event stream.  The row is split
// into internal lanes:
//
//   1. Gantt lanes for multi-day / all-day events,
//   2. per-day cells for hourly events such as PRIVATE_EXIT_TO_MAKE_UP and
//      MAKE_UP_WORK,
//   3. overflow chips (+N) when a day has more hourly events than the selected
//      compact/detailed mode should show.
//
// This solves the readability issue where many short chips were previously
// squeezed into one horizontal line and were no longer clearly assigned to a day.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import EvaluationPeriodPicker from "../../components/filters/EvaluationPeriodPicker";
import useEvaluationPeriod from "../../hooks/useEvaluationPeriod";
import { EVALUATION_PERIOD_TYPES } from "../../utils/evaluationPeriodUtils";
import { fetchAbsenceCalendar } from "../../services/absenceCalendarService";
import { fetchUsers } from "../../services/userService";

import "./AbsenceCalendarPage.css";

/* -------------------------------------------------------------------------- */
/* Constants                                                                  */
/* -------------------------------------------------------------------------- */

const BASE_GANTT_LANE_HEIGHT = 26;
const BASE_SETTLEMENT_PAIR_LANE_HEIGHT = 46;
const BASE_HOURLY_CHIP_HEIGHT = 25;
const BASE_ROW_VERTICAL_PADDING = 12;

const DEFAULT_TIMELINE_METRICS = {
  ganttLaneHeight: BASE_GANTT_LANE_HEIGHT,
  settlementPairLaneHeight: BASE_SETTLEMENT_PAIR_LANE_HEIGHT,
  hourlyChipHeight: BASE_HOURLY_CHIP_HEIGHT,
  rowVerticalPadding: BASE_ROW_VERTICAL_PADDING,
};

function getTimelineMetrics(zoomLevel) {
  // Horizontal zoom makes day columns wider.  When only the X axis grows,
  // settlement pairs start to look compressed vertically.  This derived scale
  // keeps the employee rows, lanes and time-debt chips in proportion without
  // making ordinary scrolling unusably tall.
  const safeZoom = Number.isFinite(Number(zoomLevel)) ? Math.max(1, Number(zoomLevel)) : 1;
  const verticalScale = Math.min(3.2, Math.max(1, Math.pow(safeZoom, 0.46)));

  // Typography scales more gently than the horizontal day width.  The goal is
  // to make higher zoom levels genuinely easier to read without turning the
  // monthly matrix into a poster-sized layout.  These values are exported to
  // CSS variables from the component so chips, event codes and connector labels
  // stay visually synchronized with the row/lane geometry.
  const textScale = Math.min(1.38, Math.max(1, Math.pow(safeZoom, 0.22)));
  const strokeScale = Math.min(1.55, Math.max(1, Math.pow(safeZoom, 0.18)));

  // Header and employee-column typography also scale with the same gentle
  // text curve.  Earlier versions only scaled the event chips and connection
  // labels, which made the chart feel wider after zooming but not actually
  // easier to read.  These values intentionally have upper bounds: the timeline
  // may be zoomed very far horizontally, but labels should grow only to a
  // comfortable planning-board size instead of taking over the whole screen.
  const headerScale = Math.min(1.42, Math.max(1, Math.pow(safeZoom, 0.2)));
  const headerHeightScale = Math.min(1.55, Math.max(1, Math.pow(safeZoom, 0.18)));
  const monthHeaderHeight = Math.round(28 * headerHeightScale);
  const daysHeaderHeight = Math.round(58 * headerHeightScale);

  return {
    ganttLaneHeight: Math.round(BASE_GANTT_LANE_HEIGHT * verticalScale),
    settlementPairLaneHeight: Math.round(BASE_SETTLEMENT_PAIR_LANE_HEIGHT * verticalScale),
    hourlyChipHeight: Math.round(BASE_HOURLY_CHIP_HEIGHT * verticalScale),
    rowVerticalPadding: Math.round(BASE_ROW_VERTICAL_PADDING * verticalScale),
    eventFontSizePx: Number((11.6 * textScale).toFixed(2)),
    eventCodeFontSizePx: Number((10.9 * textScale).toFixed(2)),
    hourlyFontSizePx: Number((10.4 * textScale).toFixed(2)),
    connectionLabelFontSizePx: Number((11 * textScale).toFixed(2)),
    connectionStrokeWidth: Number((4.2 * strokeScale).toFixed(2)),
    connectionHoverStrokeWidth: Number((6.4 * strokeScale).toFixed(2)),
    connectionSelectedStrokeWidth: Number((7 * strokeScale).toFixed(2)),
    monthHeaderHeightPx: monthHeaderHeight,
    daysHeaderHeightPx: daysHeaderHeight,
    headerHeightPx: monthHeaderHeight + daysHeaderHeight,
    monthHeaderFontSizePx: Number((12.2 * headerScale).toFixed(2)),
    dayNumberFontSizePx: Number((14.7 * headerScale).toFixed(2)),
    weekdayFontSizePx: Number((11.2 * headerScale).toFixed(2)),
    employeeHeaderFontSizePx: Number((12.2 * headerScale).toFixed(2)),
    employeeSummaryFontSizePx: Number((10.8 * headerScale).toFixed(2)),
    employeeNameFontSizePx: Number((13.7 * headerScale).toFixed(2)),
    employeeMetaFontSizePx: Number((11.5 * headerScale).toFixed(2)),
    matrixMetaFontSizePx: Number((13.1 * headerScale).toFixed(2)),
    matrixMetaStrongFontSizePx: Number((15.2 * headerScale).toFixed(2)),
    verticalScale,
    textScale,
    strokeScale,
    headerScale,
  };
}

const CATEGORY_OPTIONS = [
  "VACATION",
  "MEDICAL",
  "FAMILY",
  "CARE",
  "TIME_DEBT",
  "OTHER",
];

const STATUS_OPTIONS = ["APPROVED", "PENDING"];

const EMPLOYEE_SELECTION_STORAGE_KEY = "hb_visits.absenceCalendar.selectedActiveUserIds";

const BASE_DAY_COLUMN_WIDTH = 48;
const MIN_DAY_COLUMN_WIDTH = 24;
const ZOOM_STEP_FACTOR = 1.18;

// Settlement connectors intentionally use one calm, consistent blue.
// Earlier versions assigned different colors per allocation, but operators
// naturally tried to interpret those colors as business meaning.  In this
// calendar the connector color means only one thing: "this is a time-debt
// settlement relation".  The allocation amount is shown on the line label,
// while the full debt/credit balance is shown in the right-side detail panel.
const SETTLEMENT_CONNECTION_COLOR = "#2563eb";

const WEEKDAY_SHORT_PL = ["Nd", "Pn", "Wt", "Śr", "Cz", "Pt", "So"];
const WEEKDAY_SHORT_EN = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/* -------------------------------------------------------------------------- */
/* Date helpers                                                               */
/* -------------------------------------------------------------------------- */

function parseDateOnly(dateOnly) {
  const [year, month, day] = String(dateOnly || "").split("-").map(Number);
  if (!year || !month || !day) return null;

  // Local noon avoids most DST-boundary surprises when adding days.
  return new Date(year, month - 1, day, 12, 0, 0, 0);
}

function toDateOnly(dateLike) {
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "";

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function addDays(date, days) {
  const d = new Date(date.getTime());
  d.setDate(d.getDate() + days);
  return d;
}

function isLocalMidnight(dateLike) {
  if (!dateLike) return false;

  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  if (Number.isNaN(d.getTime())) return false;

  return d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0 && d.getMilliseconds() === 0;
}

function subtractDateOnly(dateOnly, days) {
  const parsed = parseDateOnly(dateOnly);
  if (!parsed) return dateOnly;
  return toDateOnly(addDays(parsed, -days));
}

function getBusinessEndDateOnly(startDate, endValue) {
  const endDate = toDateOnly(endValue || startDate);

  // The absence calendar must use local civil dates.  Backend timestamps for
  // full-day leave are often UTC instants representing local midnights.  For
  // example, Warsaw local 2026-07-30 00:00 may arrive as
  // 2026-07-29T22:00:00Z.  Slicing the raw string would produce 2026-07-29 and
  // moving the start/end independently can shift the bar to 27-29.  Instead we
  // convert both endpoints through Date/local time and only subtract one day
  // when the local endpoint is a midnight exclusive end.
  if (endDate && startDate && endDate > startDate && isLocalMidnight(endValue)) {
    return subtractDateOnly(endDate, 1);
  }

  return endDate || startDate;
}

function isDateOnly(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value || ""));
}

function getEventBusinessStartDate(event) {
  // Prefer normalized backend DTO fields when present. This is the systematic
  // guard against timezone and exclusive-end ambiguity in all calendar views.
  const explicit = event?.business_start_date || event?.start_date || event?.businessStartDate;
  if (isDateOnly(explicit)) return String(explicit);
  return toDateOnly(event?.start_at);
}

function getEventBusinessEndDate(event) {
  const explicit = event?.business_end_date || event?.end_date || event?.businessEndDate;
  if (isDateOnly(explicit)) return String(explicit);

  const startDate = getEventBusinessStartDate(event);
  return getBusinessEndDateOnly(startDate, event?.end_at || event?.start_at);
}

function addMonths(date, months) {
  const d = new Date(date.getTime());
  d.setMonth(d.getMonth() + months);
  return d;
}

function startOfMonth(dateLike) {
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  return new Date(d.getFullYear(), d.getMonth(), 1, 12, 0, 0, 0);
}

function endOfMonth(dateLike) {
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 12, 0, 0, 0);
}

function buildDays(from, to) {
  const start = parseDateOnly(from);
  const end = parseDateOnly(to);

  if (!start || !end || start > end) return [];

  const days = [];
  let cursor = start;

  while (cursor <= end) {
    const dateOnly = toDateOnly(cursor);
    const jsDay = cursor.getDay();

    days.push({
      date: new Date(cursor.getTime()),
      dateOnly,
      dayOfMonth: cursor.getDate(),
      jsDay,
      isWeekend: jsDay === 0 || jsDay === 6,
      isToday: dateOnly === toDateOnly(new Date()),
    });

    cursor = addDays(cursor, 1);
  }

  return days;
}

function buildMonthSegments(days, locale) {
  const segments = [];

  days.forEach((day, index) => {
    const monthKey = `${day.date.getFullYear()}-${String(day.date.getMonth() + 1).padStart(2, "0")}`;
    const current = segments[segments.length - 1];

    if (!current || current.monthKey !== monthKey) {
      segments.push({
        monthKey,
        startIndex: index,
        span: 1,
        label: new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(day.date),
      });
      return;
    }

    current.span += 1;
  });

  return segments;
}

function clampDateOnly(value, min, max) {
  if (!value) return min;
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

function eventDateBounds(event, rangeFrom, rangeTo) {
  const startDate = getEventBusinessStartDate(event);
  const endDate = getEventBusinessEndDate(event);

  return {
    start: clampDateOnly(startDate, rangeFrom, rangeTo),
    end: clampDateOnly(endDate, rangeFrom, rangeTo),
  };
}

function formatDateTime(dateLike, locale) {
  if (!dateLike) return "—";
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "—";

  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

function formatTime(dateLike, locale) {
  if (!dateLike) return "—";
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "—";

  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

/* -------------------------------------------------------------------------- */
/* Display helpers                                                            */
/* -------------------------------------------------------------------------- */

function formatMinutesHuman(minutes, t) {
  const total = Number(minutes || 0);
  if (!Number.isFinite(total) || total <= 0) return "0 min";

  const hours = Math.floor(total / 60);
  const mins = total % 60;

  if (hours > 0 && mins > 0) {
    return `${hours} ${t("leaves:absenceCalendar.units.hoursShort", { defaultValue: "h" })} ${mins} min`;
  }

  if (hours > 0) {
    return `${hours} ${t("leaves:absenceCalendar.units.hoursShort", { defaultValue: "h" })}`;
  }

  return `${mins} min`;
}

function formatSignedMinutesHuman(minutes, sign, t) {
  const absolute = Math.abs(Number(minutes || 0));

  // User-facing work-time balance signs are intentionally different from the
  // technical backend settlement vocabulary.  The backend still stores
  // PRIVATE_EXIT_TO_MAKE_UP as ADD_TIME_DEBT because it creates a debt that
  // must be settled.  Salon owners, however, read the HR calendar as an
  // employee work-time balance: a private exit decreases available working
  // time and make-up work increases it.  Keep that presentation rule here so
  // all Gantt labels and detail panels use the same business semantics.
  const normalizedSign = String(sign || "").trim().toLowerCase();
  const isPositive = ["credit", "increase", "plus", "makeup", "make_up_work", "allocation", "settlement"].includes(normalizedSign);
  const prefix = isPositive ? "+" : "−";

  return `${prefix}${formatMinutesHuman(absolute, t)}`;
}

function getCompactHourUnit(t) {
  // Connector labels are chart marks, not prose.  They need a dedicated, very
  // short localized unit so Polish can use "g" on the connector while detail
  // panels may keep the more descriptive "godz.".  This keeps the formatter
  // translation-driven instead of hard-coding a language-specific abbreviation
  // in component logic.
  return String(
    t("leaves:absenceCalendar.units.connectionHoursShort", {
      defaultValue: t("leaves:absenceCalendar.units.hoursShort", { defaultValue: "h" }),
    }) || "h"
  ).trim() || "h";
}

function formatMinutesChartCompact(minutes, t) {
  const total = Number(minutes || 0);
  if (!Number.isFinite(total) || total <= 0) return "0 min";

  const hours = Math.floor(total / 60);
  const mins = total % 60;
  const hourUnit = getCompactHourUnit(t);

  if (hours > 0 && mins > 0) {
    return `${hours} ${hourUnit} ${mins} min`;
  }

  if (hours > 0) {
    return `${hours} ${hourUnit}`;
  }

  return `${mins} min`;
}

function formatConnectionAllocationLabel(minutes, t) {
  return formatMinutesChartCompact(minutes, t);
}

function formatConnectionAccountingLabel(connection, t) {
  // A settlement connector should carry the accounting information that is
  // relevant at first glance.  Showing the amount only on the line, for example
  // "2 h", is not enough when one private exit of 6 h is compensated by two
  // make-up-work entries of 2 h and 4 h.  The operator needs to see both the
  // allocation and the source debt in the same visual place.
  //
  // The label therefore uses a compact language-neutral ratio:
  //   "2 h / 6 h"
  // meaning: this connection settles 2 h out of the private-exit debt of 6 h.
  // Full localized explanations remain available in the tooltip and right-hand
  // details panel, while the chart stays readable at lower zoom levels.
  const allocation = formatMinutesChartCompact(getConnectionMinutes(connection), t);
  const sourceDebtMinutes = getEventDurationMinutes(connection?.sourceEvent);

  if (sourceDebtMinutes > 0) {
    return `${allocation} / ${formatMinutesChartCompact(sourceDebtMinutes, t)}`;
  }

  return allocation;
}

function getTimeDebtEventBalanceText(event, endpointKind, fallbackSettledMinutes, t) {
  const visual = getTimeDebtVisualState(event, endpointKind, fallbackSettledMinutes);

  if (endpointKind === "debt") {
    if (visual.state === "settled") {
      return t("leaves:absenceCalendar.timeDebt.settledShort", { defaultValue: "settled" });
    }
    return t("leaves:absenceCalendar.timeDebt.openShort", {
      defaultValue: "{{value}} open",
      value: formatMinutesHuman(visual.remainingMinutes, t),
    });
  }

  if (endpointKind === "credit") {
    if (visual.state === "settled") {
      return t("leaves:absenceCalendar.timeDebt.usedShort", { defaultValue: "used" });
    }
    return t("leaves:absenceCalendar.timeDebt.availableShort", {
      defaultValue: "{{value}} left",
      value: formatMinutesHuman(visual.remainingMinutes, t),
    });
  }

  return "";
}

function formatFteDays(minutes) {
  const value = Number(minutes || 0) / 480;
  if (!Number.isFinite(value)) return "0.00";
  return value.toFixed(2);
}

function categoryLabel(category, t) {
  return t(`leaves:absenceCalendar.categories.${category}`, {
    defaultValue: category,
  });
}

function statusLabel(status, t) {
  return t(`leaves:absenceCalendar.statuses.${status}`, {
    defaultValue: status,
  });
}

function displayEventLabel(event, language) {
  if (language === "pl") return event.leave_type_label_pl || event.leave_type_label_en || event.leave_type_code;
  return event.leave_type_label_en || event.leave_type_label_pl || event.leave_type_code;
}

function getCalendarShortLabel(event, t) {
  // Backend technical codes must remain stable, but the compact labels shown in
  // the calendar are user-facing UI text.  The label therefore comes from i18n
  // first, so Polish can use natural operational abbreviations such as "WP+"
  // and "ODP" instead of English-derived "PE+" / "MW−".  Fallbacks keep the
  // calendar robust for future custom leave types that do not yet have a
  // dedicated translation key.
  const code = String(event?.leave_type_code || event?.code || "").trim().toUpperCase();

  if (code) {
    const translationKey = `absenceCalendar.shortLabels.${code}`;
    const namespacedTranslationKey = `leaves:${translationKey}`;

    // Prefer the fully namespaced i18n key.  This avoids a subtle issue where
    // the calendar may be rendered with several namespaces loaded and a
    // non-namespaced lookup can fall through to backend-provided short codes
    // such as PE+/MW-.  Those backend short codes are technical and are not
    // suitable for the HR owner-facing calendar because the owner-facing
    // convention is:
    //   PRIVATE_EXIT_TO_MAKE_UP = negative work-time balance impact
    //   MAKE_UP_WORK            = positive work-time balance impact
    // Therefore PL/EN calendar abbreviations must come from i18n, where the
    // sign convention can be expressed per locale, e.g. WP−/ODP+ in PL and
    // PE−/MW+ in EN.
    const candidates = [
      t(namespacedTranslationKey, { defaultValue: "" }),
      t(translationKey, { ns: "leaves", defaultValue: "" }),
    ];

    for (const candidate of candidates) {
      const translated = String(candidate || "").trim();

      // Some i18next configurations return the unresolved key when a
      // translation cannot be found, even when a default value is provided. The
      // calendar must never render technical i18n keys such as
      // "absenceCalendar.shortLabels.VACATION" inside event chips. Treat those
      // unresolved-key values as missing and continue to the normal fallbacks.
      if (
        translated &&
        translated !== translationKey &&
        translated !== namespacedTranslationKey &&
        !translated.includes("absenceCalendar.shortLabels.") &&
        !translated.includes("leaves:absenceCalendar.shortLabels.")
      ) {
        return translated;
      }
    }
  }

  const shortCode = String(event?.short_code || event?.shortCode || "").trim();

  // Backend short codes for the two time-debt types may still use the original
  // technical perspective (PE+ / MW−).  When a translation is missing, do not
  // display those misleading signs in the owner-facing HR calendar.  Fall
  // through to the neutral code-derived fallback instead of showing the stale
  // backend short code.
  if (shortCode && code !== "PRIVATE_EXIT_TO_MAKE_UP" && code !== "MAKE_UP_WORK") return shortCode;

  if (code) {
    const compactFromCode = code
      .split("_")
      .filter(Boolean)
      .map((part) => part.slice(0, 1))
      .join("")
      .slice(0, 4);

    return compactFromCode || code.slice(0, 4);
  }

  return "—";
}

function getWeekdayLabel(day, language) {
  return language === "pl" ? WEEKDAY_SHORT_PL[day.jsDay] : WEEKDAY_SHORT_EN[day.jsDay];
}

function normalizeEmployeeName(user) {
  if (!user) return "";
  return user.display_name || user.title || [user.first_name, user.last_name].filter(Boolean).join(" ") || user.username || `User ${user.id}`;
}

function isCalendarSelectableUser(user) {
  // The standard fetchUsers() endpoint already returns active users only. This
  // defensive check keeps the picker safe if the endpoint is ever reused with a
  // broader user DTO. Different app areas historically used slightly different
  // active flags, so we only exclude users when a known flag is explicitly
  // false/disabled.
  if (!user) return false;
  if (user.active === false || user.is_active === false || user.enabled === false) return false;
  if (String(user.status || "").toUpperCase() === "INACTIVE") return false;
  return true;
}

function readStoredEmployeeSelection() {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(EMPLOYEE_SELECTION_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;

    return parsed.map((value) => Number(value)).filter((value) => Number.isInteger(value) && value > 0);
  } catch (err) {
    console.warn("[AbsenceCalendarPage] Failed to read employee selection from localStorage:", err);
    return null;
  }
}

function storeEmployeeSelection(userIds) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(EMPLOYEE_SELECTION_STORAGE_KEY, JSON.stringify(userIds || []));
  } catch (err) {
    console.warn("[AbsenceCalendarPage] Failed to store employee selection in localStorage:", err);
  }
}

function stableNumericHash(value) {
  const text = String(value || "");
  let hash = 0;

  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) % 2147483647;
  }

  return Math.abs(hash);
}

function getConnectionKey(connection) {
  return String(
    connection?.id
      || connection?.settlement_id
      || `${connection?.source_leave_request_id || ""}-${connection?.settlement_leave_request_id || ""}-${connection?.minutes || ""}`
  );
}

function getConnectionColor() {
  return SETTLEMENT_CONNECTION_COLOR;
}

function getConnectionMinutes(connection) {
  return Number(connection?.minutes ?? connection?.settled_minutes ?? connection?.allocated_minutes ?? 0);
}

function getEventDurationMinutes(event) {
  return Math.max(0, Number(event?.duration_minutes ?? event?.durationMinutes ?? 0));
}

function getEventSettledMinutes(event, fallbackSettledMinutes = 0) {
  const direct = Number(event?.settled_minutes ?? event?.settledMinutes);
  if (Number.isFinite(direct) && direct >= 0) return direct;

  return Math.max(0, Number(fallbackSettledMinutes || 0));
}

function getEventRemainingMinutes(event, fallbackSettledMinutes = 0) {
  const direct = Number(event?.remaining_minutes ?? event?.remainingMinutes);
  if (Number.isFinite(direct) && direct >= 0) return direct;

  const duration = getEventDurationMinutes(event);
  return Math.max(0, duration - getEventSettledMinutes(event, fallbackSettledMinutes));
}

function getTimeDebtEndpointKindFromEvent(event) {
  const code = String(event?.leave_type_code || event?.code || "").toUpperCase();
  const accountingType = String(event?.balance_accounting_type || event?.balanceAccountingType || "").toUpperCase();
  const shortCode = String(event?.short_code || "").toUpperCase();

  if (code === "PRIVATE_EXIT_TO_MAKE_UP" || accountingType === "ADD_TIME_DEBT" || shortCode.startsWith("PE")) {
    return "debt";
  }

  if (code === "MAKE_UP_WORK" || accountingType === "REDUCE_TIME_DEBT" || shortCode.startsWith("MW")) {
    return "credit";
  }

  return "other";
}

function buildWorkTimeBalanceSummary(userEvents) {
  // This is intentionally a read-only calendar summary.  It does not change
  // the official leave balance, payroll data or settlement ledger.  For the
  // currently visible period and filters it answers the owner-facing question:
  // how much open private-exit debt and how much available make-up-work surplus
  // is represented by the employee's visible time-debt events.
  return (Array.isArray(userEvents) ? userEvents : []).reduce((summary, event) => {
    if (!isEffectiveCalendarEvent(event)) return summary;

    const endpointKind = getTimeDebtEndpointKindFromEvent(event);
    if (endpointKind !== "debt" && endpointKind !== "credit") return summary;

    const remainingMinutes = getEventRemainingMinutes(event);

    if (endpointKind === "debt") {
      summary.debtMinutes += remainingMinutes;
    } else {
      summary.surplusMinutes += remainingMinutes;
    }

    return summary;
  }, { debtMinutes: 0, surplusMinutes: 0 });
}

function getTimeDebtVisualState(event, endpointKind, fallbackSettledMinutes = 0) {
  const duration = getEventDurationMinutes(event);
  if (!duration) {
    return {
      state: "neutral",
      ratio: 0,
      settledMinutes: 0,
      remainingMinutes: 0,
    };
  }

  const settledMinutes = Math.min(duration, getEventSettledMinutes(event, fallbackSettledMinutes));
  const remainingMinutes = getEventRemainingMinutes(event, settledMinutes);
  const ratio = Math.max(0, Math.min(1, settledMinutes / duration));

  if (ratio >= 0.999 || remainingMinutes <= 0) {
    return { state: "settled", ratio: 1, settledMinutes, remainingMinutes: 0 };
  }

  if (ratio > 0) {
    return { state: "partial", ratio, settledMinutes, remainingMinutes };
  }

  return {
    state: endpointKind === "credit" ? "available-credit" : "open-debt",
    ratio: 0,
    settledMinutes,
    remainingMinutes,
  };
}

function buildEventHoverTitle(event, language, locale, t) {
  return [
    displayEventLabel(event, language),
    `${formatDateTime(event?.start_at, locale)} – ${formatDateTime(event?.end_at, locale)}`,
    formatMinutesHuman(event?.duration_minutes, t),
    event?.status ? String(event.status) : null,
  ].filter(Boolean).join(" · ");
}

function getSettlementRatioForEvent(connection, event) {
  const duration = getEventDurationMinutes(event);
  if (!duration) return 0;
  return Math.max(0, Math.min(1, getConnectionMinutes(connection) / duration));
}

function isSameConnection(a, b) {
  if (!a || !b) return false;
  return getConnectionKey(a) === getConnectionKey(b);
}

/* -------------------------------------------------------------------------- */
/* Event layout helpers                                                       */
/* -------------------------------------------------------------------------- */

function isEffectiveCalendarEvent(event) {
  // Defensive frontend guard.  The backend already excludes storno/cancelled
  // objects, but the calendar should remain safe if an older backend response
  // is cached or a future endpoint accidentally sends historical rows again.
  const status = String(event?.status || "").toUpperCase();
  const category = String(event?.category || "").toUpperCase();
  const code = String(event?.leave_type_code || "").toUpperCase();

  if (["CANCELLED", "REJECTED"].includes(status)) return false;
  if (category === "CANCELLATION") return false;
  if (code === "CANCELLATION" || code.includes("STORNO")) return false;

  return true;
}

function isHourlyCellEvent(event) {
  const duration = Number(event.duration_minutes || 0);
  const start = getEventBusinessStartDate(event);
  const end = getEventBusinessEndDate(event);
  const usageMode = String(event.requested_usage_mode || "").toUpperCase();

  // Time-debt events are operationally hourly even if the duration happens to
  // reach or exceed a full-time day.  In a month matrix they are much more
  // readable as day chips than as wide Gantt bars.
  if (event.is_time_debt_event) return true;

  return start === end && duration > 0 && (duration < 480 || usageMode === "HOURS");
}

function getVisibleSegment(event, days, rangeFrom, rangeTo) {
  const bounds = eventDateBounds(event, rangeFrom, rangeTo);

  const startIndex = days.findIndex((day) => day.dateOnly >= bounds.start && day.dateOnly <= bounds.end);

  let endIndex = -1;
  for (let i = days.length - 1; i >= 0; i -= 1) {
    if (days[i].dateOnly >= bounds.start && days[i].dateOnly <= bounds.end) {
      endIndex = i;
      break;
    }
  }

  if (startIndex < 0 || endIndex < 0) return null;

  return {
    startIndex,
    endIndex,
    span: Math.max(1, endIndex - startIndex + 1),
  };
}

function packGanttEventsIntoLanes(events, days, rangeFrom, rangeTo) {
  const items = events
    .map((event) => {
      const segment = getVisibleSegment(event, days, rangeFrom, rangeTo);
      return segment ? { event, ...segment } : null;
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (a.startIndex !== b.startIndex) return a.startIndex - b.startIndex;
      if (b.span !== a.span) return b.span - a.span;
      return Number(a.event.id || 0) - Number(b.event.id || 0);
    });

  const laneEndIndexes = [];

  return items.map((item) => {
    let lane = laneEndIndexes.findIndex((endIndex) => endIndex < item.startIndex);
    if (lane < 0) {
      lane = laneEndIndexes.length;
      laneEndIndexes.push(item.endIndex);
    } else {
      laneEndIndexes[lane] = item.endIndex;
    }

    return { ...item, lane };
  });
}

function groupHourlyEventsByDay(events, days) {
  const visibleDates = new Set(days.map((day) => day.dateOnly));
  const map = new Map();

  events.forEach((event) => {
    const dateOnly = getEventBusinessStartDate(event);
    if (!visibleDates.has(dateOnly)) return;

    if (!map.has(dateOnly)) map.set(dateOnly, []);
    map.get(dateOnly).push(event);
  });

  map.forEach((items) => {
    items.sort((a, b) => {
      const da = new Date(a.start_at).getTime();
      const db = new Date(b.start_at).getTime();
      if (da !== db) return da - db;
      return Number(a.id || 0) - Number(b.id || 0);
    });
  });

  return map;
}

function getEventDayIndex(event, days, rangeFrom, rangeTo) {
  const dateOnly = clampDateOnly(getEventBusinessStartDate(event), rangeFrom, rangeTo);
  return days.findIndex((day) => day.dateOnly === dateOnly);
}

function getConnectionEventIds(connection) {
  return [
    String(connection?.source_leave_request_id || connection?.sourceEvent?.id || ""),
    String(connection?.settlement_leave_request_id || connection?.settlementEvent?.id || ""),
  ].filter(Boolean);
}

function buildSettlementGroupsIntoLanes(rowConnections, days, rangeFrom, rangeTo) {
  const normalizedConnections = (Array.isArray(rowConnections) ? rowConnections : [])
    .map((connection) => {
      const sourceEvent = connection.sourceEvent;
      const settlementEvent = connection.settlementEvent;
      if (!sourceEvent || !settlementEvent) return null;
      if (!isEffectiveCalendarEvent(sourceEvent) || !isEffectiveCalendarEvent(settlementEvent)) return null;

      const sourceIndex = getEventDayIndex(sourceEvent, days, rangeFrom, rangeTo);
      const settlementIndex = getEventDayIndex(settlementEvent, days, rangeFrom, rangeTo);
      if (sourceIndex < 0 || settlementIndex < 0) return null;

      return {
        connection,
        sourceEvent,
        settlementEvent,
        sourceIndex,
        settlementIndex,
        startIndex: Math.min(sourceIndex, settlementIndex),
        endIndex: Math.max(sourceIndex, settlementIndex),
      };
    })
    .filter(Boolean);

  // The settlement table stores allocations, not visual objects.  A single
  // PRIVATE_EXIT_TO_MAKE_UP can be compensated by several MAKE_UP_WORK entries,
  // and the same make-up credit can be split across several private exits.  The
  // calendar must therefore render leave requests as unique nodes and render the
  // allocations as edges between those nodes.  If we rendered one row per edge,
  // the same private exit would be duplicated on the timeline, which makes the
  // calendar look like several real absences happened.  Grouping by connected
  // components preserves the real business state: one event node, many links.
  const eventToConnections = new Map();
  const eventById = new Map();

  normalizedConnections.forEach((item) => {
    const sourceId = String(item.sourceEvent.id);
    const settlementId = String(item.settlementEvent.id);
    eventById.set(sourceId, item.sourceEvent);
    eventById.set(settlementId, item.settlementEvent);

    [sourceId, settlementId].forEach((eventId) => {
      if (!eventToConnections.has(eventId)) eventToConnections.set(eventId, []);
      eventToConnections.get(eventId).push(item);
    });
  });

  const visitedEvents = new Set();
  const groups = [];

  eventById.forEach((_, startEventId) => {
    if (visitedEvents.has(startEventId)) return;

    const queue = [startEventId];
    const componentEventIds = new Set();
    const componentConnectionKeys = new Set();
    const componentConnections = [];

    visitedEvents.add(startEventId);

    while (queue.length > 0) {
      const eventId = queue.shift();
      componentEventIds.add(eventId);

      (eventToConnections.get(eventId) || []).forEach((item) => {
        const connectionKey = getConnectionKey(item.connection);
        if (!componentConnectionKeys.has(connectionKey)) {
          componentConnectionKeys.add(connectionKey);
          componentConnections.push(item);
        }

        [String(item.sourceEvent.id), String(item.settlementEvent.id)].forEach((nextEventId) => {
          if (!visitedEvents.has(nextEventId)) {
            visitedEvents.add(nextEventId);
            queue.push(nextEventId);
          }
        });
      });
    }

    const componentEvents = Array.from(componentEventIds)
      .map((eventId) => eventById.get(eventId))
      .filter(Boolean)
      .map((event) => ({
        event,
        dayIndex: getEventDayIndex(event, days, rangeFrom, rangeTo),
        startsAt: new Date(event.start_at).getTime(),
      }))
      .filter((item) => item.dayIndex >= 0)
      .sort((a, b) => {
        if (a.dayIndex !== b.dayIndex) return a.dayIndex - b.dayIndex;
        if (a.startsAt !== b.startsAt) return a.startsAt - b.startsAt;
        return Number(a.event.id || 0) - Number(b.event.id || 0);
      });

    if (componentEvents.length === 0) return;

    const startIndex = Math.min(...componentEvents.map((item) => item.dayIndex));
    const endIndex = Math.max(...componentEvents.map((item) => item.dayIndex));
    const key = componentConnections
      .map((item) => getConnectionKey(item.connection))
      .sort()
      .join("|") || Array.from(componentEventIds).sort().join("|");

    // The visual model must show real leave requests, not allocation rows.
    // However, a one-to-many settlement graph (one private exit compensated by
    // several make-up work entries, or the opposite) becomes hard to read when
    // every linked node is forced onto one horizontal line. We therefore keep
    // each leave request unique, but assign allocation edges to internal rows.
    // Leaf events are placed on their allocation row; hub events (degree > 1)
    // are rendered only once, approximately in the middle of related rows.
    const connectionRows = new Map();
    const eventRows = new Map();

    componentConnections
      .slice()
      .sort((a, b) => {
        if (a.startIndex !== b.startIndex) return a.startIndex - b.startIndex;
        if (a.endIndex !== b.endIndex) return a.endIndex - b.endIndex;
        return stableNumericHash(getConnectionKey(a.connection)) - stableNumericHash(getConnectionKey(b.connection));
      })
      .forEach((item, rowIndex) => {
        const connectionKey = getConnectionKey(item.connection);
        connectionRows.set(connectionKey, rowIndex);

        [String(item.sourceEvent.id), String(item.settlementEvent.id)].forEach((eventId) => {
          if (!eventRows.has(eventId)) eventRows.set(eventId, []);
          eventRows.get(eventId).push(rowIndex);
        });
      });

    const eventRowIndexes = new Map();
    eventRows.forEach((rows, eventId) => {
      const sortedRows = Array.from(new Set(rows)).sort((a, b) => a - b);
      const average = sortedRows.reduce((sum, row) => sum + row, 0) / Math.max(1, sortedRows.length);
      eventRowIndexes.set(eventId, Math.round(average));
    });

    const internalRowCount = Math.max(1, componentConnections.length || 1);

    // Split only events that land on the same day and the same internal row.
    // Nodes on different rows can keep the full day cell width, which makes
    // one-to-many PE/MW settlement graphs much clearer.
    const dayRowSlotCounts = new Map();
    const eventSlots = new Map();

    componentEvents.forEach((item) => {
      const eventId = String(item.event.id);
      const rowIndex = eventRowIndexes.get(eventId) || 0;
      const dayRowKey = `${item.dayIndex}:${rowIndex}`;
      const currentSlot = dayRowSlotCounts.get(dayRowKey) || 0;
      dayRowSlotCounts.set(dayRowKey, currentSlot + 1);
      eventSlots.set(eventId, currentSlot);
    });

    groups.push({
      key,
      events: componentEvents,
      eventSlots,
      eventRowIndexes,
      dayRowSlotCounts,
      connectionRows,
      internalRowCount,
      connections: componentConnections,
      startIndex,
      endIndex,
    });
  });

  groups.sort((a, b) => {
    if (a.startIndex !== b.startIndex) return a.startIndex - b.startIndex;
    if (a.endIndex !== b.endIndex) return a.endIndex - b.endIndex;
    return stableNumericHash(a.key) - stableNumericHash(b.key);
  });

  const laneEndIndexes = [];
  const settlementGroups = groups.map((group) => {
    const requiredRows = Math.max(1, group.internalRowCount || 1);
    let lane = -1;

    // Find a consecutive block of free settlement lanes. A settlement component
    // can be taller than one lane because one-to-many allocations are displayed
    // as a small graph: one unique hub node with several child nodes.
    for (let candidate = 0; candidate <= laneEndIndexes.length; candidate += 1) {
      let fits = true;
      for (let offset = 0; offset < requiredRows; offset += 1) {
        const occupiedUntil = laneEndIndexes[candidate + offset];
        if (occupiedUntil !== undefined && occupiedUntil >= group.startIndex) {
          fits = false;
          break;
        }
      }
      if (fits) {
        lane = candidate;
        break;
      }
    }

    if (lane < 0) lane = laneEndIndexes.length;

    for (let offset = 0; offset < requiredRows; offset += 1) {
      laneEndIndexes[lane + offset] = group.endIndex;
    }

    return { ...group, lane };
  });

  return { settlementGroups, settlementLaneCount: laneEndIndexes.length };
}

function getSettlementGroupEventKind(group, event) {
  const eventId = String(event?.id || "");
  const isDebt = group.connections.some((item) => String(item.sourceEvent.id) === eventId);
  const isCredit = group.connections.some((item) => String(item.settlementEvent.id) === eventId);

  if (isDebt && isCredit) return "mixed";
  return isDebt ? "debt" : "credit";
}

function getSettlementMinutesForEventInGroup(group, event) {
  const eventId = String(event?.id || "");

  return group.connections.reduce((sum, item) => {
    if (String(item.sourceEvent.id) === eventId || String(item.settlementEvent.id) === eventId) {
      return sum + getConnectionMinutes(item.connection);
    }
    return sum;
  }, 0);
}

function getSettlementRatioForEventInGroup(group, event) {
  const duration = getEventDurationMinutes(event);
  if (!duration) return 0;

  return Math.max(0, Math.min(1, getSettlementMinutesForEventInGroup(group, event) / duration));
}

function getSettlementGroupColor(group) {
  const firstConnection = group?.connections?.[0]?.connection;
  return firstConnection ? getConnectionColor(firstConnection) : SETTLEMENT_CONNECTION_COLOR;
}

function buildEmployeeRowLayout(userEvents, rowConnections, days, rangeFrom, rangeTo, viewMode, metrics = DEFAULT_TIMELINE_METRICS) {
  const effectiveEvents = userEvents.filter(isEffectiveCalendarEvent);

  // Time-debt allocations are visualized as connected components.  This avoids
  // duplicating a PRIVATE_EXIT_TO_MAKE_UP chip when one private exit is settled
  // by several MAKE_UP_WORK requests, or duplicating a make-up credit when it is
  // allocated to several debts.  The lane represents the real settlement graph;
  // individual leave requests remain unique nodes inside that lane.
  const { settlementGroups, settlementLaneCount } = buildSettlementGroupsIntoLanes(
    rowConnections,
    days,
    rangeFrom,
    rangeTo
  );

  const pairedEventIds = new Set();
  settlementGroups.forEach((group) => {
    group.events.forEach(({ event }) => pairedEventIds.add(String(event.id)));
  });

  const hourlyEvents = [];
  const ganttEvents = [];

  effectiveEvents.forEach((event) => {
    if (pairedEventIds.has(String(event.id))) return;
    if (isHourlyCellEvent(event)) hourlyEvents.push(event);
    else ganttEvents.push(event);
  });

  const ganttItems = packGanttEventsIntoLanes(ganttEvents, days, rangeFrom, rangeTo);
  const ganttLaneCount = ganttItems.reduce((max, item) => Math.max(max, item.lane + 1), 0);
  const hourlyByDay = groupHourlyEventsByDay(hourlyEvents, days);

  const maxHourlyVisiblePerDay = viewMode === "DETAILED" ? 4 : 2;
  const maxHourlyInAnyDay = Array.from(hourlyByDay.values()).reduce((max, items) => {
    return Math.max(max, Math.min(items.length, maxHourlyVisiblePerDay));
  }, 0);

  const ganttHeight = Math.max(ganttLaneCount, ganttItems.length > 0 ? 1 : 0) * metrics.ganttLaneHeight;
  const settlementHeight = settlementLaneCount * metrics.settlementPairLaneHeight;
  const hourlyHeight = Math.max(maxHourlyInAnyDay, hourlyEvents.length > 0 ? 1 : 0) * metrics.hourlyChipHeight;
  const gapAfterGantt = ganttHeight && settlementHeight ? 10 : 0;
  const gapAfterSettlement = (ganttHeight || settlementHeight) && hourlyHeight ? 10 : 0;
  const settlementTop = metrics.rowVerticalPadding + ganttHeight + gapAfterGantt;
  const hourlyTop = settlementTop + settlementHeight + gapAfterSettlement;
  const rowHeight = Math.max(
    62,
    metrics.rowVerticalPadding + ganttHeight + gapAfterGantt + settlementHeight + gapAfterSettlement + hourlyHeight + metrics.rowVerticalPadding
  );

  return {
    effectiveEvents,
    ganttItems,
    settlementGroups,
    settlementTop,
    settlementHeight,
    hourlyByDay,
    ganttHeight,
    hourlyTop,
    maxHourlyVisiblePerDay,
    rowHeight,
  };
}

function getSettlementGroupEventGeometry(group, eventItem, dayColumnWidth, metrics = DEFAULT_TIMELINE_METRICS) {
  const event = eventItem.event;
  const dayIndex = eventItem.dayIndex;
  const eventId = String(event.id);
  const internalRow = group.eventRowIndexes?.get(eventId) || 0;
  const laneTop = group.settlementTop + (group.lane + internalRow) * metrics.settlementPairLaneHeight;
  const chipHeight = Math.max(28, metrics.settlementPairLaneHeight - 16);
  const dayRowKey = `${dayIndex}:${internalRow}`;
  const slotCount = Math.max(1, group.dayRowSlotCounts?.get(dayRowKey) || 1);
  const slotIndex = group.eventSlots.get(eventId) || 0;
  const availableWidth = Math.max(34, dayColumnWidth - 10);
  const slotWidth = Math.max(26, availableWidth / slotCount);
  const width = Math.max(26, slotWidth - 4);
  const left = dayIndex * dayColumnWidth + 5 + slotIndex * slotWidth;

  return {
    event,
    left,
    width,
    top: laneTop + Math.max(6, Math.round((metrics.settlementPairLaneHeight - chipHeight) / 2)),
    height: chipHeight,
    centerX: left + width / 2,
    centerY: laneTop + metrics.settlementPairLaneHeight / 2,
  };
}

function getSettlementGroupEventGeometryById(group, eventId, dayColumnWidth, metrics = DEFAULT_TIMELINE_METRICS) {
  const eventItem = group.events.find((item) => String(item.event.id) === String(eventId));
  if (!eventItem) return null;
  return getSettlementGroupEventGeometry(group, eventItem, dayColumnWidth, metrics);
}

function buildEventPositionMap(layout, days, dayColumnWidth, metrics = DEFAULT_TIMELINE_METRICS) {
  const positions = new Map();

  layout.ganttItems.forEach((item) => {
    const centerX = item.startIndex * dayColumnWidth + (item.span * dayColumnWidth) / 2;
    const centerY = metrics.rowVerticalPadding + item.lane * metrics.ganttLaneHeight + metrics.ganttLaneHeight / 2;
    positions.set(String(item.event.id), { x: centerX, y: centerY, kind: "bar" });
  });

  layout.settlementGroups.forEach((group) => {
    const enriched = { ...group, settlementTop: layout.settlementTop };
    group.events.forEach((eventItem) => {
      const geometry = getSettlementGroupEventGeometry(enriched, eventItem, dayColumnWidth, metrics);
      positions.set(String(eventItem.event.id), { x: geometry.centerX, y: geometry.centerY, kind: "settlement" });
    });
  });

  days.forEach((day, dayIndex) => {
    const visible = (layout.hourlyByDay.get(day.dateOnly) || []).slice(0, layout.maxHourlyVisiblePerDay);
    visible.forEach((event, stackIndex) => {
      positions.set(String(event.id), {
        x: dayIndex * dayColumnWidth + dayColumnWidth / 2,
        y: layout.hourlyTop + stackIndex * metrics.hourlyChipHeight + metrics.hourlyChipHeight / 2,
        kind: "chip",
      });
    });
  });

  return positions;
}

function buildConnectionPositionMap(layout, dayColumnWidth, metrics = DEFAULT_TIMELINE_METRICS) {
  const positions = new Map();

  layout.settlementGroups.forEach((group) => {
    const enriched = { ...group, settlementTop: layout.settlementTop };

    group.connections.forEach((item) => {
      const source = getSettlementGroupEventGeometryById(enriched, item.sourceEvent.id, dayColumnWidth, metrics);
      const settlement = getSettlementGroupEventGeometryById(enriched, item.settlementEvent.id, dayColumnWidth, metrics);
      if (!source || !settlement) return;

      positions.set(getConnectionKey(item.connection), {
        from: { x: source.centerX, y: source.centerY },
        to: { x: settlement.centerX, y: settlement.centerY },
      });
    });
  });

  return positions;
}

/* -------------------------------------------------------------------------- */
/* Main component                                                             */
/* -------------------------------------------------------------------------- */

const AbsenceCalendarPage = () => {
  const { t, i18n } = useTranslation(["leaves", "navigation", "reports", "common"]);
  const navigate = useNavigate();

  const language = i18n.language?.startsWith("pl") ? "pl" : "en";
  const locale = language === "pl" ? "pl-PL" : "en-US";

  const evaluationPeriod = useEvaluationPeriod({
    // Absence planning is most useful when HR sees adjacent months as one
    // continuous timeline.  Three months is therefore the default period: it
    // keeps the current month in context and avoids the operator having to
    // immediately switch away from the old one-month default.
    defaultMode: EVALUATION_PERIOD_TYPES.THREE_MONTHS,
    initialDate: new Date(),
    allowedModes: [
      EVALUATION_PERIOD_TYPES.MONTH,
      EVALUATION_PERIOD_TYPES.THREE_MONTHS,
      EVALUATION_PERIOD_TYPES.SIX_MONTHS,
      EVALUATION_PERIOD_TYPES.YEAR,
      EVALUATION_PERIOD_TYPES.WEEK,
      EVALUATION_PERIOD_TYPES.DAY,
      EVALUATION_PERIOD_TYPES.MANUAL,
    ],
  });

  const { from: selectedFrom, to: selectedTo } = evaluationPeriod;

  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [isEmployeeSelectionReady, setIsEmployeeSelectionReady] = useState(false);
  const [isEmployeePickerOpen, setIsEmployeePickerOpen] = useState(false);
  const [employeePickerSearch, setEmployeePickerSearch] = useState("");
  const [draftSelectedUserIds, setDraftSelectedUserIds] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState(["APPROVED", "PENDING"]);
  const [viewMode, setViewMode] = useState("COMPACT");
  const [showWeekends, setShowWeekends] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [showConnections, setShowConnections] = useState(true);
  const [hoveredConnectionKey, setHoveredConnectionKey] = useState(null);
  const [isPanningTimeline, setIsPanningTimeline] = useState(false);

  const [calendarData, setCalendarData] = useState({ users: [], events: [], connections: [], range: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedConnection, setSelectedConnection] = useState(null);

  const abortRef = useRef(null);
  const scrollRef = useRef(null);

  // Connection hover has a small "magnetic" retention window.  The SVG
  // connector hit target is intentionally wider than the visible line, but
  // operators still often move the cursor by a few pixels while trying to
  // click a thin timeline relation.  Delaying hover release makes the line
  // feel sticky enough to target without making the calendar feel laggy.
  const connectionHoverHoldRef = useRef(null);

  const panRef = useRef({
    active: false,
    moved: false,
    startX: 0,
    startY: 0,
    startScrollLeft: 0,
    startScrollTop: 0,
  });
  const suppressNextClickRef = useRef(false);

  const days = useMemo(() => {
    const raw = buildDays(selectedFrom, selectedTo);
    return showWeekends ? raw : raw.filter((day) => !day.isWeekend);
  }, [selectedFrom, selectedTo, showWeekends]);

  const dayColumnWidth = Math.max(MIN_DAY_COLUMN_WIDTH, Math.round(BASE_DAY_COLUMN_WIDTH * zoomLevel));
  const timelineMetrics = useMemo(() => getTimelineMetrics(zoomLevel), [zoomLevel]);
  const gridWidth = Math.max(days.length * dayColumnWidth, 1);
  const monthSegments = useMemo(() => buildMonthSegments(days, locale), [days, locale]);
  const zoomPercent = Math.round((dayColumnWidth / BASE_DAY_COLUMN_WIDTH) * 100);

  /* ------------------------------------------------------------------------ */
  /* Load active employee list for the modal browser                           */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    let active = true;

    async function loadUsers() {
      try {
        const users = await fetchUsers();
        if (!active) return;

        const activeUsers = (Array.isArray(users) ? users : []).filter(isCalendarSelectableUser);
        const activeIds = activeUsers
          .map((user) => Number(user.id))
          .filter((id) => Number.isInteger(id) && id > 0);
        const storedIds = readStoredEmployeeSelection();
        const activeIdSet = new Set(activeIds);
        const nextSelectedIds = Array.isArray(storedIds)
          ? storedIds.filter((id) => activeIdSet.has(Number(id)))
          : activeIds;

        // By default the absence calendar is a global HR planning view, so all
        // active employees are selected. If the operator previously saved a
        // custom selection, we restore only ids that still exist and are active.
        setAllUsers(activeUsers);
        setSelectedUserIds(nextSelectedIds);
        setIsEmployeeSelectionReady(true);
      } catch (err) {
        console.error("[AbsenceCalendarPage] Failed to load users:", err);
        setIsEmployeeSelectionReady(true);
      }
    }

    loadUsers();

    return () => {
      active = false;
    };
  }, []);

  /* ------------------------------------------------------------------------ */
  /* Load calendar read model                                                 */
  /* ------------------------------------------------------------------------ */

  const loadCalendar = useCallback(async () => {
    if (!selectedFrom || !selectedTo) return;

    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError("");

    try {
      const result = await fetchAbsenceCalendar(
        {
          from: selectedFrom,
          to: selectedTo,
          userIds: isEmployeeSelectionReady ? selectedUserIds : [],
          statuses: selectedStatuses,
          categories: selectedCategories,
          includeCancelled: false,
        },
        { signal: controller.signal }
      );

      const selectedUserSet = new Set((isEmployeeSelectionReady ? selectedUserIds : []).map((id) => Number(id)));
      const effectiveEvents = Array.isArray(result.events)
        ? result.events
            .filter(isEffectiveCalendarEvent)
            .filter((event) => !isEmployeeSelectionReady || selectedUserSet.has(Number(event.user_id)))
        : [];
      const visibleIds = new Set(effectiveEvents.map((event) => String(event.id)));
      const effectiveResult = {
        ...result,
        events: effectiveEvents,
        connections: Array.isArray(result.connections)
          ? result.connections.filter((connection) => visibleIds.has(String(connection.source_leave_request_id)) && visibleIds.has(String(connection.settlement_leave_request_id)))
          : [],
      };

      setCalendarData(effectiveResult);
      setSelectedEvent((previous) => {
        if (!previous) return null;
        return effectiveResult.events.find((event) => String(event.id) === String(previous.id)) || null;
      });
      setSelectedConnection((previous) => {
        if (!previous) return null;
        const nextConnection = effectiveResult.connections?.find((connection) => isSameConnection(connection, previous));
        if (!nextConnection) return null;

        const eventById = new Map(effectiveResult.events.map((event) => [String(event.id), event]));
        return {
          ...nextConnection,
          sourceEvent: eventById.get(String(nextConnection.source_leave_request_id)),
          settlementEvent: eventById.get(String(nextConnection.settlement_leave_request_id)),
        };
      });
    } catch (err) {
      if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError" || err?.name === "AbortError") {
        return;
      }

      console.error("[AbsenceCalendarPage] Failed to load absence calendar:", err);
      setError(t("leaves:absenceCalendar.errors.failedToLoad", { defaultValue: "Failed to load absence calendar." }));
      setCalendarData({ users: [], events: [], connections: [], range: null });
    } finally {
      setLoading(false);
    }
  }, [selectedFrom, selectedTo, selectedUserIds, selectedStatuses, selectedCategories, isEmployeeSelectionReady, t]);

  useEffect(() => {
    loadCalendar();

    return () => {
      if (abortRef.current) abortRef.current.abort();
      if (connectionHoverHoldRef.current) {
        window.clearTimeout(connectionHoverHoldRef.current);
        connectionHoverHoldRef.current = null;
      }
    };
  }, [loadCalendar]);

  /* ------------------------------------------------------------------------ */
  /* Derived datasets                                                         */
  /* ------------------------------------------------------------------------ */

  const usersForMatrix = useMemo(() => {
    const byId = new Map();

    calendarData.users.forEach((user) => {
      byId.set(Number(user.id), {
        ...user,
        display_name: normalizeEmployeeName(user),
      });
    });

    // If the calendar endpoint currently has no events for a selected user, the
    // user should still remain visible.  This is important for planning: an
    // empty row is meaningful information.
    allUsers.forEach((user) => {
      const id = Number(user.id);
      if (!Number.isFinite(id)) return;
      if (isEmployeeSelectionReady && !selectedUserIds.includes(id)) return;
      if (byId.has(id)) return;

      byId.set(id, {
        id,
        display_name: normalizeEmployeeName(user),
        username: user.username || null,
        role: user.role || null,
      });
    });

    return Array.from(byId.values()).sort((a, b) => {
      return normalizeEmployeeName(a).localeCompare(normalizeEmployeeName(b), locale);
    });
  }, [calendarData.users, allUsers, selectedUserIds, isEmployeeSelectionReady, locale]);

  const eventsByUser = useMemo(() => {
    const map = new Map();

    calendarData.events.filter(isEffectiveCalendarEvent).forEach((event) => {
      const userId = Number(event.user_id);
      if (!map.has(userId)) map.set(userId, []);
      map.get(userId).push(event);
    });

    Array.from(map.values()).forEach((events) => {
      events.sort((a, b) => {
        const da = new Date(a.start_at).getTime();
        const db = new Date(b.start_at).getTime();
        if (da !== db) return da - db;
        return Number(a.id || 0) - Number(b.id || 0);
      });
    });

    return map;
  }, [calendarData.events]);

  const visibleEventsCount = useMemo(() => {
    const selectedUserSet = new Set((isEmployeeSelectionReady ? selectedUserIds : []).map((id) => Number(id)));
    return calendarData.events
      .filter(isEffectiveCalendarEvent)
      .filter((event) => !isEmployeeSelectionReady || selectedUserSet.has(Number(event.user_id)))
      .length;
  }, [calendarData.events, selectedUserIds, isEmployeeSelectionReady]);
  const connectionsByUser = useMemo(() => {
    const eventById = new Map(calendarData.events.map((event) => [String(event.id), event]));
    const map = new Map();

    (calendarData.connections || []).forEach((connection) => {
      const sourceEvent = eventById.get(String(connection.source_leave_request_id));
      const settlementEvent = eventById.get(String(connection.settlement_leave_request_id));
      if (!sourceEvent || !settlementEvent) return;
      if (Number(sourceEvent.user_id) !== Number(settlementEvent.user_id)) return;

      const userId = Number(sourceEvent.user_id);
      if (!map.has(userId)) map.set(userId, []);
      map.get(userId).push({ ...connection, sourceEvent, settlementEvent });
    });

    return map;
  }, [calendarData.events, calendarData.connections]);


  /* ------------------------------------------------------------------------ */
  /* Filter handlers                                                          */
  /* ------------------------------------------------------------------------ */

  function toggleArrayValue(values, value) {
    return values.includes(value)
      ? values.filter((item) => item !== value)
      : [...values, value];
  }

  function toggleCategory(category) {
    setSelectedCategories((prev) => toggleArrayValue(prev, category));
  }

  function toggleStatus(status) {
    setSelectedStatuses((prev) => {
      const next = toggleArrayValue(prev, status);
      return next.length > 0 ? next : ["APPROVED"];
    });
  }

  const selectedEmployeeSummary = useMemo(() => {
    const selectedCount = isEmployeeSelectionReady ? selectedUserIds.length : allUsers.length;
    const totalCount = allUsers.length;

    if (!totalCount) {
      return t("leaves:absenceCalendar.employeeSelection.noneAvailable", { defaultValue: "No active employees" });
    }

    if (selectedCount === totalCount) {
      return t("leaves:absenceCalendar.employeeSelection.allSelected", { defaultValue: "All employees" });
    }

    return t("leaves:absenceCalendar.employeeSelection.selectedOfTotal", {
      selected: selectedCount,
      total: totalCount,
      defaultValue: `${selectedCount} of ${totalCount}`,
    });
  }, [allUsers.length, selectedUserIds.length, isEmployeeSelectionReady, t]);

  const filteredEmployeePickerUsers = useMemo(() => {
    const query = employeePickerSearch.trim().toLowerCase();
    const sortedUsers = [...allUsers].sort((a, b) => normalizeEmployeeName(a).localeCompare(normalizeEmployeeName(b), locale));

    if (!query) return sortedUsers;

    return sortedUsers.filter((user) => {
      const haystack = [
        normalizeEmployeeName(user),
        user.username,
        user.email,
        user.role,
      ].filter(Boolean).join(" ").toLowerCase();

      return haystack.includes(query);
    });
  }, [allUsers, employeePickerSearch, locale]);

  function openEmployeePicker() {
    setDraftSelectedUserIds(selectedUserIds);
    setEmployeePickerSearch("");
    setIsEmployeePickerOpen(true);
  }

  function closeEmployeePicker() {
    setIsEmployeePickerOpen(false);
    setEmployeePickerSearch("");
  }

  function toggleDraftEmployee(userId) {
    const id = Number(userId);
    if (!Number.isInteger(id) || id <= 0) return;

    setDraftSelectedUserIds((previous) => (
      previous.includes(id)
        ? previous.filter((item) => item !== id)
        : [...previous, id]
    ));
  }

  function selectAllDraftEmployees() {
    setDraftSelectedUserIds(
      allUsers
        .map((user) => Number(user.id))
        .filter((id) => Number.isInteger(id) && id > 0)
    );
  }

  function clearDraftEmployees() {
    setDraftSelectedUserIds([]);
  }

  function applyEmployeePicker() {
    const activeIdSet = new Set(allUsers.map((user) => Number(user.id)));
    const normalizedIds = Array.from(new Set(
      draftSelectedUserIds
        .map((id) => Number(id))
        .filter((id) => Number.isInteger(id) && id > 0 && activeIdSet.has(id))
    ));

    setSelectedUserIds(normalizedIds);
    storeEmployeeSelection(normalizedIds);
    setIsEmployeeSelectionReady(true);
    closeEmployeePicker();
  }

  function updateTimelineZoom(nextUpdater, anchorClientX = null) {
    const scrollNode = scrollRef.current;
    const previousWidth = dayColumnWidth;
    const previousScrollLeft = scrollNode?.scrollLeft || 0;
    const anchorOffset = scrollNode && anchorClientX != null
      ? Math.max(0, anchorClientX - scrollNode.getBoundingClientRect().left)
      : scrollNode
        ? scrollNode.clientWidth / 2
        : 0;
    const anchorTimelineX = previousScrollLeft + anchorOffset;
    const anchorRatio = previousWidth > 0 ? anchorTimelineX / previousWidth : 0;

    setZoomLevel((previous) => {
      const rawNext = typeof nextUpdater === "function" ? nextUpdater(previous) : Number(nextUpdater);
      const next = Number.isFinite(rawNext) ? Math.max(MIN_DAY_COLUMN_WIDTH / BASE_DAY_COLUMN_WIDTH, rawNext) : previous;

      window.requestAnimationFrame(() => {
        if (!scrollNode) return;
        const nextWidth = Math.max(MIN_DAY_COLUMN_WIDTH, Math.round(BASE_DAY_COLUMN_WIDTH * next));
        scrollNode.scrollLeft = Math.max(0, anchorRatio * nextWidth - anchorOffset);
      });

      return next;
    });
  }

  function zoomIn(anchorClientX = null) {
    updateTimelineZoom((previous) => previous * ZOOM_STEP_FACTOR, anchorClientX);
  }

  function zoomOut(anchorClientX = null) {
    updateTimelineZoom((previous) => previous / ZOOM_STEP_FACTOR, anchorClientX);
  }

  function resetZoom() {
    updateTimelineZoom(1);
  }

  function handleTimelineWheel(event) {
    const scrollNode = scrollRef.current;
    if (!scrollNode) return;

    // Wheel semantics for a dense planning matrix:
    // - plain wheel remains native vertical scrolling; this is the most common
    //   action when many employees are visible,
    // - Shift + wheel performs horizontal scrolling across the date axis,
    // - Alt + wheel zooms the time axis around the mouse cursor,
    // - Ctrl + wheel is intentionally left to the browser/system zoom gesture.
    if (event.altKey && !event.ctrlKey) {
      event.preventDefault();
      if (event.deltaY < 0) zoomIn(event.clientX);
      else zoomOut(event.clientX);
      return;
    }

    if (event.shiftKey && !event.ctrlKey) {
      event.preventDefault();
      scrollNode.scrollLeft += event.deltaY || event.deltaX;
    }
  }

  function isTimelinePanIgnoredTarget(target) {
    if (!target || typeof target.closest !== "function") return false;

    // Do not start drag-to-pan from interactive controls.  The matrix contains
    // event chips, connection paths, buttons and links; those controls must
    // remain clickable.  Drag-to-pan starts only from neutral grid background
    // or from a deliberate drag gesture on the timeline surface.
    return Boolean(target.closest(
      "button, a, input, select, textarea, summary, details, [role='button'], [data-absence-interactive='true']"
    ));
  }

  function isTimelinePanSurface(target) {
    if (!target || typeof target.closest !== "function") return false;

    // Drag-to-pan is a Gantt/timeline affordance.  It should not make the whole
    // page feel draggable and it should never start from the sticky employee
    // label column or from ordinary toolbar/detail areas.  The accepted target
    // set below is intentionally limited to the horizontal date axis and the
    // employee timeline grid where users expect map-like panning.
    return Boolean(target.closest(
      ".absence-calendar-date-header, .absence-calendar-row-timeline, .absence-calendar-day-cell, .absence-calendar-gantt-layer, .absence-calendar-hourly-grid"
    )) && !Boolean(target.closest(
      ".absence-calendar-employee-header, .absence-calendar-employee-cell, .absence-calendar-matrix-meta"
    ));
  }

  function handleTimelineMouseDown(event) {
    const scrollNode = scrollRef.current;
    if (
      !scrollNode
      || event.button !== 0
      || isTimelinePanIgnoredTarget(event.target)
      || !isTimelinePanSurface(event.target)
    ) return;

    panRef.current = {
      active: true,
      moved: false,
      startX: event.clientX,
      startY: event.clientY,
      startScrollLeft: scrollNode.scrollLeft,
      startScrollTop: scrollNode.scrollTop,
    };
  }

  function handleTimelineMouseMove(event) {
    const scrollNode = scrollRef.current;
    const pan = panRef.current;
    if (!scrollNode || !pan.active) return;

    const dx = event.clientX - pan.startX;
    const dy = event.clientY - pan.startY;

    if (!pan.moved && Math.abs(dx) + Math.abs(dy) < 4) return;

    pan.moved = true;
    suppressNextClickRef.current = true;
    setIsPanningTimeline(true);

    // Grab-to-pan follows the common map/Gantt interaction model: dragging the
    // mouse to the right reveals earlier dates, dragging down reveals earlier
    // employees.  This is intentionally separate from wheel scrolling so users
    // can choose the most comfortable navigation style for large zoom levels.
    scrollNode.scrollLeft = pan.startScrollLeft - dx;
    scrollNode.scrollTop = pan.startScrollTop - dy;
    event.preventDefault();
  }

  function finishTimelinePan() {
    if (!panRef.current.active) return;
    const moved = panRef.current.moved;
    panRef.current.active = false;
    panRef.current.moved = false;
    setIsPanningTimeline(false);

    if (moved) {
      window.setTimeout(() => {
        suppressNextClickRef.current = false;
      }, 0);
    }
  }

  function handleTimelineClickCapture(event) {
    if (!suppressNextClickRef.current) return;

    suppressNextClickRef.current = false;
    event.preventDefault();
    event.stopPropagation();
  }

  function handleTimelineBackgroundClick(event) {
    if (suppressNextClickRef.current) return;

    // Selecting a settlement connector intentionally dims the rest of the
    // connector layer and opens the right-side details panel.  Operators
    // naturally expect to leave that inspection mode by clicking back into the
    // neutral Gantt area, not only by targeting the close button in the panel.
    //
    // Keep clicks on real controls untouched: events, connector hit paths,
    // toolbar buttons, links and form controls all keep their own handlers.
    // Only a click on the timeline background/day grid clears the current
    // selection and restores the full connector label visibility.
    if (isTimelinePanIgnoredTarget(event.target)) return;
    if (!isTimelinePanSurface(event.target)) return;

    if (selectedEvent || selectedConnection) {
      clearSelection();
    }
  }

  function selectEvent(event) {
    setSelectedConnection(null);
    setSelectedEvent(event);
  }

  function selectConnection(connection) {
    setSelectedEvent(null);
    setSelectedConnection(connection);
  }

  function holdConnectionHover(key) {
    if (connectionHoverHoldRef.current) {
      window.clearTimeout(connectionHoverHoldRef.current);
      connectionHoverHoldRef.current = null;
    }
    setHoveredConnectionKey(key);
  }

  function releaseConnectionHover(key) {
    if (connectionHoverHoldRef.current) {
      window.clearTimeout(connectionHoverHoldRef.current);
    }

    connectionHoverHoldRef.current = window.setTimeout(() => {
      setHoveredConnectionKey((previous) => (previous === key ? null : previous));
      connectionHoverHoldRef.current = null;
    }, 220);
  }

  function clearSelection() {
    setSelectedEvent(null);
    setSelectedConnection(null);
  }

  function isEventLinkedToSelectedConnection(event) {
    if (!event || !selectedConnection) return false;
    const eventId = String(event.id);
    return eventId === String(selectedConnection.source_leave_request_id)
      || eventId === String(selectedConnection.settlement_leave_request_id);
  }

  function isEventLinkedToHoveredConnection(event) {
    if (!event || !hoveredConnectionKey) return false;
    const connection = (calendarData.connections || []).find((candidate) => getConnectionKey(candidate) === hoveredConnectionKey);
    if (!connection) return false;
    const eventId = String(event.id);
    return eventId === String(connection.source_leave_request_id)
      || eventId === String(connection.settlement_leave_request_id);
  }

  /* ------------------------------------------------------------------------ */
  /* Render helpers                                                           */
  /* ------------------------------------------------------------------------ */

  function renderGanttEvent(item) {
    const event = item.event;
    const left = item.startIndex * dayColumnWidth + 5;
    const width = Math.max(38, item.span * dayColumnWidth - 10);
    const top = timelineMetrics.rowVerticalPadding + item.lane * timelineMetrics.ganttLaneHeight;

    const classes = [
      "absence-calendar-event",
      `absence-calendar-event--${String(event.category || "OTHER").toLowerCase()}`,
      `absence-calendar-event--${String(event.status_style || "solid").toLowerCase()}`,
      "absence-calendar-event--bar",
      selectedEvent?.id === event.id || isEventLinkedToSelectedConnection(event) ? "absence-calendar-event--selected" : "",
      isEventLinkedToHoveredConnection(event) ? "absence-calendar-event--connection-hover" : "",
    ].filter(Boolean).join(" ");

    return (
      <button
        key={event.id}
        type="button"
        className={classes}
        style={{ left, width, top, height: Math.max(22, timelineMetrics.ganttLaneHeight - 4) }}
        title={buildEventHoverTitle(event, language, locale, t)}
        onClick={() => selectEvent(event)}
      >
        <span className="absence-calendar-event-code">{getCalendarShortLabel(event, t)}</span>
        <span className="absence-calendar-event-label">{displayEventLabel(event, language)}</span>
      </button>
    );
  }

  function renderSettlementGroupEventChip(group, eventItem) {
    const enriched = { ...group, settlementTop: group.settlementTop ?? 0 };
    const geometry = getSettlementGroupEventGeometry(enriched, eventItem, dayColumnWidth, timelineMetrics);
    const event = geometry.event;
    const connectionColor = getSettlementGroupColor(group);
    const endpointKind = getSettlementGroupEventKind(group, event);
    const fallbackSettledMinutes = getSettlementMinutesForEventInGroup(group, event);
    const settlementVisual = getTimeDebtVisualState(event, endpointKind, fallbackSettledMinutes);
    const settlementRatio = settlementVisual.ratio;
    const classes = [
      "absence-calendar-settlement-pair-chip",
      endpointKind === "debt" ? "absence-calendar-settlement-pair-chip--debt" : "",
      endpointKind === "credit" ? "absence-calendar-settlement-pair-chip--credit" : "",
      endpointKind === "mixed" ? "absence-calendar-settlement-pair-chip--mixed" : "",
      settlementVisual.state === "open-debt" ? "absence-calendar-settlement-pair-chip--open-debt" : "",
      settlementVisual.state === "available-credit" ? "absence-calendar-settlement-pair-chip--available-credit" : "",
      settlementVisual.state === "settled" ? "absence-calendar-settlement-pair-chip--fully-settled" : "",
      settlementVisual.state === "partial" ? "absence-calendar-settlement-pair-chip--partially-settled" : "",
      `absence-calendar-event--${String(event.category || "TIME_DEBT").toLowerCase()}`,
      `absence-calendar-event--${String(event.status_style || "solid").toLowerCase()}`,
      selectedEvent?.id === event.id || isEventLinkedToSelectedConnection(event) ? "absence-calendar-event--selected" : "",
      isEventLinkedToHoveredConnection(event) ? "absence-calendar-event--connection-hover" : "",
    ].filter(Boolean).join(" ");

    return (
      <button
        key={`${group.key}-event-${event.id}`}
        type="button"
        className={classes}
        style={{
          left: geometry.left,
          width: geometry.width,
          top: geometry.top,
          height: geometry.height,
          "--connection-color": connectionColor,
          "--settled-ratio": settlementVisual.state === "open-debt" || settlementVisual.state === "available-credit"
            ? "100%"
            : `${Math.round(settlementRatio * 100)}%`,
        }}
        title={buildEventHoverTitle(event, language, locale, t)}
        onClick={() => selectEvent(event)}
      >
        <span className="absence-calendar-settlement-pair-marker" />
        <span className="absence-calendar-event-code">{getCalendarShortLabel(event, t)}</span>

      </button>
    );
  }

  function renderHourlyChip(event, compact = false) {
    const endpointKind = event.is_time_debt_event ? getTimeDebtEndpointKindFromEvent(event) : "other";
    const settlementVisual = event.is_time_debt_event
      ? getTimeDebtVisualState(event, endpointKind, 0)
      : { state: "neutral", ratio: 0 };
    const classes = [
      "absence-calendar-hourly-chip",
      event.is_time_debt_event ? "absence-calendar-hourly-chip--time-debt" : "",
      settlementVisual.state === "open-debt" ? "absence-calendar-settlement-pair-chip--open-debt" : "",
      settlementVisual.state === "available-credit" ? "absence-calendar-settlement-pair-chip--available-credit" : "",
      settlementVisual.state === "settled" ? "absence-calendar-settlement-pair-chip--fully-settled" : "",
      settlementVisual.state === "partial" ? "absence-calendar-settlement-pair-chip--partially-settled" : "",
      `absence-calendar-event--${String(event.category || "OTHER").toLowerCase()}`,
      `absence-calendar-event--${String(event.status_style || "solid").toLowerCase()}`,
      selectedEvent?.id === event.id || isEventLinkedToSelectedConnection(event) ? "absence-calendar-event--selected" : "",
      isEventLinkedToHoveredConnection(event) ? "absence-calendar-event--connection-hover" : "",
    ].filter(Boolean).join(" ");

    const timeLabel = `${formatTime(event.start_at, locale)}–${formatTime(event.end_at, locale)}`;

    return (
      <button
        key={event.id}
        type="button"
        className={classes}
        style={event.is_time_debt_event ? {
          "--settled-ratio": settlementVisual.state === "open-debt" || settlementVisual.state === "available-credit"
            ? "100%"
            : `${Math.round((settlementVisual.ratio || 0) * 100)}%`,
        } : undefined}
        title={buildEventHoverTitle(event, language, locale, t)}
        onClick={() => selectEvent(event)}
      >
        <span className="absence-calendar-event-code">{getCalendarShortLabel(event, t)}</span>
        {!compact && <span className="absence-calendar-hourly-chip-time">{timeLabel}</span>}
      </button>
    );
  }

  function renderSettlementConnections(rowConnections, connectionPositionMap, rowHeight) {
    if (!showConnections || !Array.isArray(rowConnections) || rowConnections.length === 0) return null;

    const drawable = rowConnections
      .map((connection, index) => {
        const key = getConnectionKey(connection);
        const endpoints = connectionPositionMap.get(key);
        if (!endpoints?.from || !endpoints?.to) return null;

        const from = endpoints.from;
        const to = endpoints.to;
        const direction = to.x >= from.x ? 1 : -1;
        const distance = Math.abs(to.x - from.x);

        // Connected time-debt events are now packed into the same settlement
        // lane.  The connector therefore stays on that lane and only uses a
        // very small curve to remain visible.  This is intentionally much less
        // "spider-web like" than drawing arbitrary arcs between unrelated chip
        // stacks.
        const arc = 10 + (index % 3) * 3;
        const controlOffset = Math.max(24, distance * 0.28);
        const controlY = Math.max(6, Math.min(from.y, to.y) - arc);
        const c1x = from.x + direction * controlOffset;
        const c2x = to.x - direction * controlOffset;
        const path = `M ${from.x} ${from.y} C ${c1x} ${controlY}, ${c2x} ${controlY}, ${to.x} ${to.y}`;
        const color = getConnectionColor(connection);
        const isSelected = isSameConnection(connection, selectedConnection);
        const isHovered = hoveredConnectionKey === key;
        const isMuted = Boolean(selectedConnection) && !isSelected;
        const label = formatConnectionAccountingLabel(connection, t);

        // Keep the label anchored to the actual Bezier connector instead of
        // changing the text or using SVG scaling on hover.  SVG transforms scale
        // from the viewport origin by default, which made the label appear to
        // "run away" to the right when the connector was hovered.  A stable
        // midpoint label avoids that navigation problem and keeps the amount in
        // the same place in normal, hover and selected states.
        const midX = (from.x + 3 * c1x + 3 * c2x + to.x) / 8;
        const midY = (from.y + 3 * controlY + 3 * controlY + to.y) / 8;
        const labelX = Math.max(18, Math.min(gridWidth - 18, midX));
        const labelY = Math.max(16, Math.min(rowHeight - 14, midY - 6));
        const labelWidth = Math.max(44, Math.min(136, label.length * (timelineMetrics.connectionLabelFontSizePx * 0.62) + 18));

        return { connection, key, path, color, isSelected, isHovered, isMuted, label, labelX, labelY, labelWidth };
      })
      .filter(Boolean);

    if (drawable.length === 0) return null;

    return (
      <svg
        className="absence-calendar-connection-layer"
        width={gridWidth}
        height={rowHeight}
        viewBox={`0 0 ${gridWidth} ${rowHeight}`}
        aria-label={t("leaves:absenceCalendar.connectionLayer", { defaultValue: "Time-debt settlement connections" })}
      >
        <defs>
          {drawable.map(({ key, color }) => (
            <marker
              key={`marker-${key}`}
              id={`absence-calendar-connection-arrow-${key.replace(/[^a-zA-Z0-9_-]/g, "_")}`}
              markerWidth="8"
              markerHeight="8"
              refX="6"
              refY="3"
              orient="auto"
              markerUnits="strokeWidth"
            >
              <path d="M0,0 L0,6 L7,3 z" style={{ fill: color }} />
            </marker>
          ))}
        </defs>
        {drawable.map(({ connection, key, path, color, isSelected, isHovered, isMuted, label, labelX, labelY, labelWidth }) => {
          const markerId = `absence-calendar-connection-arrow-${key.replace(/[^a-zA-Z0-9_-]/g, "_")}`;
          const classes = [
            "absence-calendar-connection-path",
            isSelected ? "absence-calendar-connection-path--selected" : "",
            isHovered ? "absence-calendar-connection-path--hovered" : "",
            isMuted ? "absence-calendar-connection-path--muted" : "",
          ].filter(Boolean).join(" ");

          return (
            <g key={key} className="absence-calendar-connection-group" style={{ "--connection-color": color }}>
              {/*
                The visible connector remains visually precise, but the
                transparent hit path below it is intentionally much thicker.
                This follows the common interaction pattern used by timeline
                and diagram tools: do not make users click the exact visual
                stroke; give the relation a generous interaction corridor.
              */}
              <title>
                {t("leaves:absenceCalendar.connectionTooltip", {
                  defaultValue: "This make-up allocation restores {{allocation}} of the {{balanceDecrease}} private-exit decrease.",
                  allocation: formatMinutesHuman(getConnectionMinutes(connection), t),
                  balanceDecrease: formatMinutesHuman(getEventDurationMinutes(connection?.sourceEvent), t),
                })}
              </title>
              <path
                className="absence-calendar-connection-hit-path"
                d={path}
                role="button"
                tabIndex={0}
                aria-label={t("leaves:absenceCalendar.connectionAriaLabel", { defaultValue: "Open settlement connection details" })}
                onClick={(event) => {
                  event.stopPropagation();
                  selectConnection(connection);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    selectConnection(connection);
                  }
                }}
                onMouseEnter={() => holdConnectionHover(key)}
                onMouseMove={() => holdConnectionHover(key)}
                onMouseLeave={() => releaseConnectionHover(key)}
              />
              <path
                className={classes}
                d={path}
                markerEnd={`url(#${markerId})`}
                aria-hidden="true"
              />
              <g
                className={[
                  "absence-calendar-connection-label",
                  isSelected ? "absence-calendar-connection-label--selected" : "",
                  isHovered ? "absence-calendar-connection-label--hovered" : "",
                  isMuted ? "absence-calendar-connection-label--muted" : "",
                ].filter(Boolean).join(" ")}
                aria-hidden="true"
              >
                <rect
                  x={labelX - labelWidth / 2}
                  y={labelY - 10}
                  width={labelWidth}
                  height="18"
                  rx="9"
                />
                <text x={labelX} y={labelY + 3}>{label}</text>
              </g>
            </g>
          );
        })}
      </svg>
    );
  }

  function renderConnectionDetailPanel() {
    const connection = selectedConnection;
    const sourceEvent = connection?.sourceEvent;
    const settlementEvent = connection?.settlementEvent;
    const color = getConnectionColor(connection);
    const minutes = getConnectionMinutes(connection);

    // Build the connected settlement component for the selected allocation.
    // The panel should not only describe the clicked line; it should also
    // answer the operational question HR actually has: how much debt/credit is
    // still open across all PE+/MW- items that participate in the same visual
    // settlement graph.  The backend remains the source of truth for request
    // status and minutes; this is a read-only calendar summary.
    const selectedConnectionKey = getConnectionKey(connection);
    const eventById = new Map((calendarData.events || []).map((event) => [String(event.id), event]));
    const allEnrichedConnections = (calendarData.connections || [])
      .map((item) => {
        const nextSource = eventById.get(String(item.source_leave_request_id));
        const nextSettlement = eventById.get(String(item.settlement_leave_request_id));
        if (!nextSource || !nextSettlement) return null;
        if (Number(nextSource.user_id) !== Number(nextSettlement.user_id)) return null;
        return { ...item, sourceEvent: nextSource, settlementEvent: nextSettlement };
      })
      .filter(Boolean);

    const queue = [String(connection?.source_leave_request_id || ""), String(connection?.settlement_leave_request_id || "")].filter(Boolean);
    const visitedEventIds = new Set(queue);
    const componentConnections = [];
    const visitedConnectionKeys = new Set();

    while (queue.length > 0) {
      const eventId = queue.shift();

      allEnrichedConnections.forEach((item) => {
        const itemKey = getConnectionKey(item);
        const sourceId = String(item.source_leave_request_id);
        const settlementId = String(item.settlement_leave_request_id);
        if (sourceId !== eventId && settlementId !== eventId) return;

        if (!visitedConnectionKeys.has(itemKey)) {
          visitedConnectionKeys.add(itemKey);
          componentConnections.push(item);
        }

        [sourceId, settlementId].forEach((nextEventId) => {
          if (!visitedEventIds.has(nextEventId)) {
            visitedEventIds.add(nextEventId);
            queue.push(nextEventId);
          }
        });
      });
    }

    const debtEvents = Array.from(new Set(componentConnections.map((item) => String(item.source_leave_request_id))))
      .map((id) => eventById.get(id))
      .filter(Boolean);
    const creditEvents = Array.from(new Set(componentConnections.map((item) => String(item.settlement_leave_request_id))))
      .map((id) => eventById.get(id))
      .filter(Boolean);
    const settledTotal = componentConnections.reduce((sum, item) => sum + getConnectionMinutes(item), 0);
    const originalDebt = debtEvents.reduce((sum, event) => sum + getEventDurationMinutes(event), 0);
    const creditTotal = creditEvents.reduce((sum, event) => sum + getEventDurationMinutes(event), 0);
    const openDebt = debtEvents.reduce((sum, event) => sum + getEventRemainingMinutes(event), 0);
    const availableCredit = creditEvents.reduce((sum, event) => sum + getEventRemainingMinutes(event), 0);
    const usedCredit = Math.max(0, creditTotal - availableCredit);

    const selectedDebtOriginal = sourceEvent ? getEventDurationMinutes(sourceEvent) : 0;
    const selectedDebtSettledTotal = sourceEvent
      ? componentConnections
        .filter((item) => String(item.source_leave_request_id) === String(sourceEvent.id))
        .reduce((sum, item) => sum + getConnectionMinutes(item), 0)
      : 0;
    const selectedDebtOtherSettled = Math.max(0, selectedDebtSettledTotal - minutes);
    const selectedDebtOpen = Math.max(0, selectedDebtOriginal - selectedDebtSettledTotal);

    const selectedCreditOriginal = settlementEvent ? getEventDurationMinutes(settlementEvent) : 0;
    const selectedCreditUsedTotal = settlementEvent
      ? componentConnections
        .filter((item) => String(item.settlement_leave_request_id) === String(settlementEvent.id))
        .reduce((sum, item) => sum + getConnectionMinutes(item), 0)
      : 0;
    const selectedCreditOtherUsed = Math.max(0, selectedCreditUsedTotal - minutes);
    const selectedCreditAvailable = Math.max(0, selectedCreditOriginal - selectedCreditUsedTotal);

    return (
      <aside className="absence-calendar-details absence-calendar-details--connection" style={{ "--connection-color": color }}>
        <div className="absence-calendar-details-header">
          <div>
            <p className="absence-calendar-eyebrow">
              {t("leaves:absenceCalendar.details.connectionEyebrow", { defaultValue: "Time settlement connection" })}
            </p>
            <h2>{t("leaves:absenceCalendar.details.connectionTitle", { defaultValue: "Private exit ↔ make-up work" })}</h2>
          </div>
          <button
            type="button"
            className="absence-calendar-close-details"
            onClick={clearSelection}
            aria-label={t("common:close", { defaultValue: "Close" })}
          >
            ×
          </button>
        </div>

        <div className="absence-calendar-connection-summary">
          <span className="absence-calendar-connection-color-dot" />
          <div>
            <strong>{formatConnectionAllocationLabel(minutes, t)}</strong>
            <small>
              {t("leaves:absenceCalendar.details.connectionSummary", {
                defaultValue: "allocation shown on this connection",
              })}
            </small>
          </div>
        </div>

        <div className="absence-calendar-linked-events">
          <div className="absence-calendar-linked-event-card absence-calendar-linked-event-card--debt">
            <span>{t("leaves:absenceCalendar.details.privateExit", { defaultValue: "Private exit to make up" })}</span>
            <strong>{sourceEvent ? displayEventLabel(sourceEvent, language) : "—"}</strong>
            <small>{sourceEvent ? `${formatDateTime(sourceEvent.start_at, locale)} – ${formatDateTime(sourceEvent.end_at, locale)}` : "—"}</small>
            {sourceEvent && (
              <em>{t("leaves:absenceCalendar.details.privateExitBalanceImpact", { defaultValue: "Work-time balance decrease" })}: {formatSignedMinutesHuman(selectedDebtOriginal, "debt", t)}</em>
            )}
            {sourceEvent && (
              <button type="button" onClick={() => navigate(sourceEvent.route || `/work-items/leave:${sourceEvent.id}`)}>
                {t("leaves:absenceCalendar.details.openPrivateExit", { defaultValue: "Open private exit" })}
              </button>
            )}
          </div>

          <div className="absence-calendar-linked-event-card absence-calendar-linked-event-card--credit">
            <span>{t("leaves:absenceCalendar.details.makeUpWork", { defaultValue: "Make-up work" })}</span>
            <strong>{settlementEvent ? displayEventLabel(settlementEvent, language) : "—"}</strong>
            <small>{settlementEvent ? `${formatDateTime(settlementEvent.start_at, locale)} – ${formatDateTime(settlementEvent.end_at, locale)}` : "—"}</small>
            {settlementEvent && (
              <em>{t("leaves:absenceCalendar.details.makeUpBalanceImpact", { defaultValue: "Work-time balance increase" })}: {formatSignedMinutesHuman(selectedCreditOriginal, "credit", t)}</em>
            )}
            {settlementEvent && (
              <button type="button" onClick={() => navigate(settlementEvent.route || `/work-items/leave:${settlementEvent.id}`)}>
                {t("leaves:absenceCalendar.details.openMakeUpWork", { defaultValue: "Open make-up work" })}
              </button>
            )}
          </div>
        </div>

        <div className="absence-calendar-detail-grid absence-calendar-detail-grid--compact absence-calendar-connection-meta">
          <div>
            <span>{t("leaves:absenceCalendar.details.settlementMode", { defaultValue: "Settlement mode" })}</span>
            <strong>{connection?.settlement_mode || connection?.mode || "—"}</strong>
          </div>
          <div>
            <span>{t("leaves:absenceCalendar.details.allocationState", { defaultValue: "Allocation state" })}</span>
            <strong>{connection?.allocation_state || connection?.state || "ACTIVE"}</strong>
          </div>
          <div>
            <span>{t("leaves:absenceCalendar.details.settled", { defaultValue: "Settled" })}</span>
            <strong>{formatMinutesHuman(minutes, t)}</strong>
          </div>
        </div>

        <div className="absence-calendar-settlement-balance-summary">
          <h3>{t("leaves:absenceCalendar.details.settlementBalance", { defaultValue: "Settlement balance" })}</h3>
          <div className="absence-calendar-balance-ledger">
            <div>
              <span>{t("leaves:absenceCalendar.details.privateExitBalanceDecrease", { defaultValue: "Private exit balance decrease" })}</span>
              <strong className="absence-calendar-ledger-minus">{formatSignedMinutesHuman(selectedDebtOriginal, "debt", t)}</strong>
            </div>
            <div>
              <span>{t("leaves:absenceCalendar.details.thisMakeUpAllocation", { defaultValue: "This make-up allocation" })}</span>
              <strong className="absence-calendar-ledger-plus">{formatSignedMinutesHuman(minutes, "increase", t)}</strong>
            </div>
            <div>
              <span>{t("leaves:absenceCalendar.details.otherMakeUpAllocations", { defaultValue: "Other make-up allocations" })}</span>
              <strong className="absence-calendar-ledger-plus">{formatSignedMinutesHuman(selectedDebtOtherSettled, "increase", t)}</strong>
            </div>
            <div className="absence-calendar-balance-ledger-total">
              <span>{t("leaves:absenceCalendar.details.remainingOpenBalance", { defaultValue: "Remaining open balance" })}</span>
              <strong className={selectedDebtOpen > 0 ? "absence-calendar-ledger-minus" : "absence-calendar-ledger-zero"}>
                {selectedDebtOpen > 0 ? formatSignedMinutesHuman(selectedDebtOpen, "debt", t) : formatMinutesHuman(0, t)}
              </strong>
            </div>
          </div>
          <div className="absence-calendar-balance-ledger absence-calendar-balance-ledger--credit">
            <div>
              <span>{t("leaves:absenceCalendar.details.makeUpBalanceIncrease", { defaultValue: "Make-up work balance increase" })}</span>
              <strong className="absence-calendar-ledger-plus">{formatSignedMinutesHuman(selectedCreditOriginal, "credit", t)}</strong>
            </div>
            <div>
              <span>{t("leaves:absenceCalendar.details.usedByThisAllocation", { defaultValue: "Used by this allocation" })}</span>
              <strong className="absence-calendar-ledger-plus">{formatSignedMinutesHuman(minutes, "increase", t)}</strong>
            </div>
            <div>
              <span>{t("leaves:absenceCalendar.details.usedByOtherAllocations", { defaultValue: "Used by other allocations" })}</span>
              <strong className="absence-calendar-ledger-plus">{formatSignedMinutesHuman(selectedCreditOtherUsed, "increase", t)}</strong>
            </div>
            <div className="absence-calendar-balance-ledger-total">
              <span>{t("leaves:absenceCalendar.details.availableSurplus", { defaultValue: "Available surplus" })}</span>
              <strong className={selectedCreditAvailable > 0 ? "absence-calendar-ledger-plus" : "absence-calendar-ledger-zero"}>
                {selectedCreditAvailable > 0 ? formatSignedMinutesHuman(selectedCreditAvailable, "credit", t) : formatMinutesHuman(0, t)}
              </strong>
            </div>
          </div>
        </div>

        <div className="absence-calendar-settlement-group-summary">
          <h3>{t("leaves:absenceCalendar.details.groupSummary", { defaultValue: "Settlement group summary" })}</h3>
          <div className="absence-calendar-detail-grid absence-calendar-detail-grid--compact">
            <div>
              <span>{t("leaves:absenceCalendar.details.originalBalanceDecrease", { defaultValue: "Original balance decrease" })}</span>
              <strong className="absence-calendar-ledger-minus">{originalDebt > 0 ? formatSignedMinutesHuman(originalDebt, "debt", t) : formatMinutesHuman(0, t)}</strong>
            </div>
            <div>
              <span>{t("leaves:absenceCalendar.details.makeUpAllocatedTotal", { defaultValue: "Make-up allocated total" })}</span>
              <strong className="absence-calendar-ledger-plus">{settledTotal > 0 ? formatSignedMinutesHuman(settledTotal, "increase", t) : formatMinutesHuman(0, t)}</strong>
            </div>
            <div>
              <span>{t("leaves:absenceCalendar.details.openBalance", { defaultValue: "Open balance" })}</span>
              <strong className={openDebt > 0 ? "absence-calendar-ledger-minus" : "absence-calendar-ledger-zero"}>{openDebt > 0 ? formatSignedMinutesHuman(openDebt, "debt", t) : formatMinutesHuman(0, t)}</strong>
            </div>
            <div>
              <span>{t("leaves:absenceCalendar.details.makeUpSurplusTotal", { defaultValue: "Make-up surplus total" })}</span>
              <strong className="absence-calendar-ledger-plus">{creditTotal > 0 ? formatSignedMinutesHuman(creditTotal, "credit", t) : formatMinutesHuman(0, t)}</strong>
            </div>
            <div>
              <span>{t("leaves:absenceCalendar.details.usedSurplus", { defaultValue: "Used surplus" })}</span>
              <strong className="absence-calendar-ledger-plus">{usedCredit > 0 ? formatSignedMinutesHuman(usedCredit, "increase", t) : formatMinutesHuman(0, t)}</strong>
            </div>
            <div>
              <span>{t("leaves:absenceCalendar.details.availableSurplus", { defaultValue: "Available surplus" })}</span>
              <strong className={availableCredit > 0 ? "absence-calendar-ledger-plus" : "absence-calendar-ledger-zero"}>{availableCredit > 0 ? formatSignedMinutesHuman(availableCredit, "credit", t) : formatMinutesHuman(0, t)}</strong>
            </div>
          </div>
        </div>

        {componentConnections.length > 1 && (
          <div className="absence-calendar-related-allocations">
            <h3>{t("leaves:absenceCalendar.details.relatedAllocations", { defaultValue: "Related allocations" })}</h3>
            <ul>
              {componentConnections.map((item) => {
                const itemKey = getConnectionKey(item);
                const isCurrent = itemKey === selectedConnectionKey;

                return (
                  <li key={itemKey} className={isCurrent ? "active" : ""}>
                    <span className="absence-calendar-related-allocation-color" style={{ background: getConnectionColor(item) }} />
                    <div>
                      <strong>{formatConnectionAllocationLabel(getConnectionMinutes(item), t)}</strong>
                      <small>
                        {formatDateTime(item.sourceEvent?.start_at, locale)} → {formatDateTime(item.settlementEvent?.start_at, locale)}
                      </small>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </aside>
    );
  }

  function renderDetailPanel() {
    if (selectedConnection) {
      return renderConnectionDetailPanel();
    }

    if (!selectedEvent) {
      return (
        <aside className="absence-calendar-details absence-calendar-details--empty">
          <h2>{t("leaves:absenceCalendar.details.title", { defaultValue: "Event details" })}</h2>
          <p>{t("leaves:absenceCalendar.details.empty", { defaultValue: "Select an event or a connector line in the matrix to see details." })}</p>
        </aside>
      );
    }

    const event = selectedEvent;

    return (
      <aside className="absence-calendar-details">
        <div className="absence-calendar-details-header">
          <div>
            <p className="absence-calendar-eyebrow">{categoryLabel(event.category, t)}</p>
            <h2>{displayEventLabel(event, language)}</h2>
          </div>
          <button
            type="button"
            className="absence-calendar-close-details"
            onClick={clearSelection}
            aria-label={t("common:close", { defaultValue: "Close" })}
          >
            ×
          </button>
        </div>

        <div className="absence-calendar-detail-grid">
          <div>
            <span>{t("leaves:absenceCalendar.details.employee", { defaultValue: "Employee" })}</span>
            <strong>{event.employee_name}</strong>
          </div>
          <div>
            <span>{t("leaves:absenceCalendar.details.status", { defaultValue: "Status" })}</span>
            <strong>{statusLabel(event.status, t)}</strong>
          </div>
          <div>
            <span>{t("leaves:absenceCalendar.details.from", { defaultValue: "From" })}</span>
            <strong>{formatDateTime(event.start_at, locale)}</strong>
          </div>
          <div>
            <span>{t("leaves:absenceCalendar.details.to", { defaultValue: "To" })}</span>
            <strong>{formatDateTime(event.end_at, locale)}</strong>
          </div>
          <div>
            <span>{t("leaves:absenceCalendar.details.duration", { defaultValue: "Duration" })}</span>
            <strong>{formatMinutesHuman(event.duration_minutes, t)}</strong>
            <small>{event.duration_minutes} min · {formatFteDays(event.duration_minutes)} FTE d</small>
          </div>
          <div>
            <span>{t("leaves:absenceCalendar.details.code", { defaultValue: "Code" })}</span>
            <strong>{event.leave_type_code}</strong>
          </div>
        </div>

        {event.is_time_debt_event && (
          <div className="absence-calendar-settlement-box">
            <h3>{t("leaves:absenceCalendar.details.timeSettlement", { defaultValue: "Work-time settlement" })}</h3>
            <div className="absence-calendar-detail-grid absence-calendar-detail-grid--compact">
              <div>
                <span>{t("leaves:absenceCalendar.details.settlementStatus", { defaultValue: "Settlement status" })}</span>
                <strong>{event.settlement_status || "—"}</strong>
              </div>
              <div>
                <span>{t("leaves:absenceCalendar.details.settled", { defaultValue: "Settled" })}</span>
                <strong>{formatMinutesHuman(event.settled_minutes, t)}</strong>
              </div>
              <div>
                <span>{t("leaves:absenceCalendar.details.remaining", { defaultValue: "Remaining" })}</span>
                <strong>{event.remaining_minutes == null ? "—" : formatMinutesHuman(event.remaining_minutes, t)}</strong>
              </div>
            </div>
          </div>
        )}

        {event.is_time_debt_event && (
          <div className="absence-calendar-settlement-box absence-calendar-settlement-box--links">
            <h3>{t("leaves:absenceCalendar.details.linkedEvents", { defaultValue: "Linked make-up events" })}</h3>
            <p>{t("leaves:absenceCalendar.details.linkedEventsHint", { defaultValue: "Colored connector lines in the calendar show active allocations between private exits and make-up work. Click a line to inspect the two linked events." })}</p>
          </div>
        )}

        {event.note && (
          <div className="absence-calendar-note">
            <span>{t("leaves:absenceCalendar.details.note", { defaultValue: "Note" })}</span>
            <p>{event.note}</p>
          </div>
        )}

        <button
          type="button"
          className="absence-calendar-open-work-item"
          onClick={() => navigate(event.route || `/work-items/leave:${event.id}`)}
        >
          {t("leaves:absenceCalendar.details.openWorkItem", { defaultValue: "Open work item" })}
        </button>
      </aside>
    );
  }

  /* ------------------------------------------------------------------------ */
  /* Render                                                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <div className="absence-calendar-page">
      <header className="absence-calendar-page-header">
        <div>
          <h1>{t("leaves:absenceCalendar.title", { defaultValue: "Absence Calendar" })}</h1>
          <p>{t("leaves:absenceCalendar.subtitle", { defaultValue: "Absences and work-time adjustments by employee." })}</p>
        </div>
      </header>

      <section className="absence-calendar-toolbar" aria-label={t("leaves:absenceCalendar.filters", { defaultValue: "Calendar filters" })}>
        <div className="absence-calendar-toolbar-main">
          <div className="absence-calendar-period-stack">
            <EvaluationPeriodPicker period={evaluationPeriod} />
          </div>

          <div className="absence-calendar-toolbar-actions">
            <button
              type="button"
              className="absence-calendar-refresh-button"
              onClick={loadCalendar}
              disabled={loading || !selectedFrom || !selectedTo || !isEmployeeSelectionReady}
              title={t("leaves:absenceCalendar.refreshTitle", { defaultValue: "Reload the absence calendar data without leaving this page." })}
            >
              <span aria-hidden="true">↻</span>
              {loading
                ? t("common:loading", { defaultValue: "Loading..." })
                : t("common:refresh", { defaultValue: "Refresh" })}
            </button>
          </div>
        </div>

        <div className="absence-calendar-filter-row">
          <div className="absence-calendar-toggle-group" aria-label={t("leaves:absenceCalendar.filterCategories", { defaultValue: "Categories" })}>
            <span>{t("leaves:absenceCalendar.filterCategories", { defaultValue: "Categories" })}</span>
            {CATEGORY_OPTIONS.map((category) => (
              <button
                key={category}
                type="button"
                className={selectedCategories.includes(category) ? "active" : ""}
                onClick={() => toggleCategory(category)}
              >
                {categoryLabel(category, t)}
              </button>
            ))}
          </div>

          <div className="absence-calendar-toggle-group" aria-label={t("leaves:absenceCalendar.filterStatuses", { defaultValue: "Statuses" })}>
            <span>{t("leaves:absenceCalendar.filterStatuses", { defaultValue: "Statuses" })}</span>
            {STATUS_OPTIONS.map((status) => (
              <button
                key={status}
                type="button"
                className={selectedStatuses.includes(status) ? "active" : ""}
                onClick={() => toggleStatus(status)}
              >
                {statusLabel(status, t)}
              </button>
            ))}
          </div>

          <div className="absence-calendar-view-controls">
            <button
              type="button"
              className={viewMode === "COMPACT" ? "active" : ""}
              onClick={() => setViewMode("COMPACT")}
            >
              {t("leaves:absenceCalendar.compact", { defaultValue: "Compact" })}
            </button>
            <button
              type="button"
              className={viewMode === "DETAILED" ? "active" : ""}
              onClick={() => setViewMode("DETAILED")}
            >
              {t("leaves:absenceCalendar.detailed", { defaultValue: "Detailed" })}
            </button>
            <div className="absence-calendar-zoom-controls" role="group" aria-label={t("leaves:absenceCalendar.zoom", { defaultValue: "Zoom" })}>
              <span>{t("leaves:absenceCalendar.zoom", { defaultValue: "Zoom" })}</span>
              <button type="button" onClick={() => zoomOut()} title={t("leaves:absenceCalendar.zoomOut", { defaultValue: "Zoom out" })}>−</button>
              <button type="button" onClick={resetZoom} title={t("leaves:absenceCalendar.resetZoom", { defaultValue: "Reset zoom" })}>{zoomPercent}%</button>
              <button type="button" onClick={() => zoomIn()} title={t("leaves:absenceCalendar.zoomIn", { defaultValue: "Zoom in" })}>+</button>
              <button type="button" onClick={() => updateTimelineZoom((previous) => previous * ZOOM_STEP_FACTOR * ZOOM_STEP_FACTOR)} title={t("leaves:absenceCalendar.zoomInMore", { defaultValue: "Zoom in more" })}>++</button>
              <small>{t("leaves:absenceCalendar.zoomWheelHint", { defaultValue: "Alt + wheel zooms · Shift + wheel scrolls horizontally" })}</small>
            </div>
            <label className="absence-calendar-checkbox">
              <input
                type="checkbox"
                checked={showConnections}
                onChange={(event) => setShowConnections(event.target.checked)}
              />
              {t("leaves:absenceCalendar.showConnections", { defaultValue: "Connections" })}
            </label>
            <label className="absence-calendar-checkbox">
              <input
                type="checkbox"
                checked={showWeekends}
                onChange={(event) => setShowWeekends(event.target.checked)}
              />
              {t("leaves:absenceCalendar.showWeekends", { defaultValue: "Weekends" })}
            </label>
          </div>
        </div>
      </section>

      <section className="absence-calendar-legend" aria-label={t("leaves:absenceCalendar.legend", { defaultValue: "Legend" })}>
        {CATEGORY_OPTIONS.map((category) => (
          <span key={category} className={`absence-calendar-legend-item absence-calendar-legend-item--${category.toLowerCase()}`}>
            <i /> {categoryLabel(category, t)}
          </span>
        ))}
        <span className="absence-calendar-status-sample absence-calendar-status-sample--pending">
          {t("leaves:absenceCalendar.statusHintPending", { defaultValue: "Dashed = pending" })}
        </span>
      </section>

      {error && <div className="absence-calendar-error">{error}</div>}

      <main
        className={`absence-calendar-workspace absence-calendar-workspace--${viewMode.toLowerCase()}`}
        style={{
          "--absence-event-font-size": `${timelineMetrics.eventFontSizePx}px`,
          "--absence-event-code-font-size": `${timelineMetrics.eventCodeFontSizePx}px`,
          "--absence-hourly-font-size": `${timelineMetrics.hourlyFontSizePx}px`,
          "--absence-connection-label-font-size": `${timelineMetrics.connectionLabelFontSizePx}px`,
          "--absence-connection-stroke-width": timelineMetrics.connectionStrokeWidth,
          "--absence-connection-hover-stroke-width": timelineMetrics.connectionHoverStrokeWidth,
          "--absence-connection-selected-stroke-width": timelineMetrics.connectionSelectedStrokeWidth,
          "--absence-month-header-height": `${timelineMetrics.monthHeaderHeightPx}px`,
          "--absence-days-header-height": `${timelineMetrics.daysHeaderHeightPx}px`,
          "--absence-header-height": `${timelineMetrics.headerHeightPx}px`,
          "--absence-month-header-font-size": `${timelineMetrics.monthHeaderFontSizePx}px`,
          "--absence-day-number-font-size": `${timelineMetrics.dayNumberFontSizePx}px`,
          "--absence-weekday-font-size": `${timelineMetrics.weekdayFontSizePx}px`,
          "--absence-employee-header-font-size": `${timelineMetrics.employeeHeaderFontSizePx}px`,
          "--absence-employee-summary-font-size": `${timelineMetrics.employeeSummaryFontSizePx}px`,
          "--absence-employee-name-font-size": `${timelineMetrics.employeeNameFontSizePx}px`,
          "--absence-employee-meta-font-size": `${timelineMetrics.employeeMetaFontSizePx}px`,
          "--absence-matrix-meta-font-size": `${timelineMetrics.matrixMetaFontSizePx}px`,
          "--absence-matrix-meta-strong-font-size": `${timelineMetrics.matrixMetaStrongFontSizePx}px`,
        }}
      >
        <section className="absence-calendar-matrix-card">
          <div className="absence-calendar-matrix-meta">
            <strong>{evaluationPeriod.displayLabel || `${selectedFrom} – ${selectedTo}`}</strong>
            <span>{visibleEventsCount} {t("leaves:absenceCalendar.events", { defaultValue: "events" })}</span>
            <span>{t("leaves:absenceCalendar.employeeSelection.summaryPrefix", { defaultValue: "Employees" })}: {selectedEmployeeSummary}</span>
            {loading && <span>{t("common:loading", { defaultValue: "Loading..." })}</span>}
          </div>

          <div
            className={`absence-calendar-scroll ${isPanningTimeline ? "absence-calendar-scroll--panning" : ""}`}
            ref={scrollRef}
            onWheel={handleTimelineWheel}
            onMouseDown={handleTimelineMouseDown}
            onMouseMove={handleTimelineMouseMove}
            onMouseUp={finishTimelinePan}
            onMouseLeave={finishTimelinePan}
            onClickCapture={handleTimelineClickCapture}
            onClick={handleTimelineBackgroundClick}
            title={t("leaves:absenceCalendar.panHint", { defaultValue: "Drag the Gantt/timeline area to pan. Alt + wheel zooms. Shift + wheel scrolls horizontally." })}
          >
            <div className="absence-calendar-grid" style={{ width: gridWidth + 240 }}>
              <div className="absence-calendar-grid-header">
                <div className="absence-calendar-employee-header">
                  <div>
                    <span>{t("leaves:absenceCalendar.employee", { defaultValue: "Employee" })}</span>
                    <small>{selectedEmployeeSummary}</small>
                  </div>
                  <button
                    type="button"
                    className="absence-calendar-employee-change-button"
                    onClick={openEmployeePicker}
                  >
                    {t("leaves:absenceCalendar.employeeSelection.change", { defaultValue: "Change" })}
                  </button>
                </div>
                <div className="absence-calendar-date-header" style={{ width: gridWidth }}>
                  <div className="absence-calendar-month-header-row" style={{ width: gridWidth }}>
                    {monthSegments.map((segment) => (
                      <div
                        key={segment.monthKey}
                        className="absence-calendar-month-header"
                        style={{ left: segment.startIndex * dayColumnWidth, width: segment.span * dayColumnWidth }}
                        title={segment.label}
                      >
                        {segment.label}
                      </div>
                    ))}
                  </div>
                  <div className="absence-calendar-days-header" style={{ width: gridWidth }}>
                    {days.map((day) => (
                      <div
                        key={day.dateOnly}
                        className={[
                          "absence-calendar-day-header",
                          day.isWeekend ? "absence-calendar-day-header--weekend" : "",
                          day.isToday ? "absence-calendar-day-header--today" : "",
                        ].filter(Boolean).join(" ")}
                        style={{ width: dayColumnWidth }}
                      >
                        <strong>{day.dayOfMonth}</strong>
                        <span>{getWeekdayLabel(day, language)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {usersForMatrix.map((user) => {
                const userEvents = eventsByUser.get(Number(user.id)) || [];
                const rowConnections = connectionsByUser.get(Number(user.id)) || [];
                const layout = buildEmployeeRowLayout(userEvents, rowConnections, days, selectedFrom, selectedTo, viewMode, timelineMetrics);
                const connectionPositionMap = buildConnectionPositionMap(layout, dayColumnWidth, timelineMetrics);
                const balanceSummary = buildWorkTimeBalanceSummary(userEvents);
                const hasTimeBalanceSummary = balanceSummary.debtMinutes > 0 || balanceSummary.surplusMinutes > 0;

                return (
                  <div key={user.id} className="absence-calendar-row" style={{ minHeight: layout.rowHeight }}>
                    <div className="absence-calendar-employee-cell" style={{ minHeight: layout.rowHeight }}>
                      <strong>{normalizeEmployeeName(user)}</strong>
                      {user.username && <span>{user.username}</span>}
                      {hasTimeBalanceSummary && (
                        <div
                          className="absence-calendar-employee-balance-summary"
                          aria-label={t("leaves:absenceCalendar.employeeBalanceSummaryAria", {
                            defaultValue: "Open time-debt and surplus summary for the selected period",
                          })}
                        >
                          {balanceSummary.debtMinutes > 0 && (
                            <span
                              className="absence-calendar-balance-pill absence-calendar-balance-pill--debt"
                              title={t("leaves:absenceCalendar.employeeOpenDebtTitle", {
                                defaultValue: "Open private-exit debt in the selected period",
                              })}
                            >
                              {t("leaves:absenceCalendar.employeeOpenDebtShort", { defaultValue: "Debt" })}
                              <strong>{formatSignedMinutesHuman(balanceSummary.debtMinutes, "debt", t)}</strong>
                            </span>
                          )}
                          {balanceSummary.surplusMinutes > 0 && (
                            <span
                              className="absence-calendar-balance-pill absence-calendar-balance-pill--surplus"
                              title={t("leaves:absenceCalendar.employeeSurplusTitle", {
                                defaultValue: "Available make-up-work surplus in the selected period",
                              })}
                            >
                              {t("leaves:absenceCalendar.employeeSurplusShort", { defaultValue: "Surplus" })}
                              <strong>{formatSignedMinutesHuman(balanceSummary.surplusMinutes, "credit", t)}</strong>
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="absence-calendar-row-timeline" style={{ width: gridWidth, minHeight: layout.rowHeight }}>
                      {days.map((day) => (
                        <div
                          key={day.dateOnly}
                          className={[
                            "absence-calendar-day-cell",
                            day.isWeekend ? "absence-calendar-day-cell--weekend" : "",
                            day.isToday ? "absence-calendar-day-cell--today" : "",
                          ].filter(Boolean).join(" ")}
                          style={{ width: dayColumnWidth, minHeight: layout.rowHeight }}
                        />
                      ))}

                      <div className="absence-calendar-gantt-layer">
                        {layout.ganttItems.map(renderGanttEvent)}
                      </div>

                      {renderSettlementConnections(rowConnections, connectionPositionMap, layout.rowHeight)}

                      <div className="absence-calendar-settlement-pair-layer">
                        {layout.settlementGroups.flatMap((group) => {
                          const enrichedGroup = { ...group, settlementTop: layout.settlementTop };
                          return group.events.map((eventItem) => renderSettlementGroupEventChip(enrichedGroup, eventItem));
                        })}
                      </div>

                      <div className="absence-calendar-hourly-grid" style={{ top: layout.hourlyTop }}>
                        {days.map((day) => {
                          const items = layout.hourlyByDay.get(day.dateOnly) || [];
                          const visible = items.slice(0, layout.maxHourlyVisiblePerDay);
                          const hiddenCount = Math.max(0, items.length - visible.length);

                          return (
                            <div key={day.dateOnly} className="absence-calendar-hourly-cell" style={{ width: dayColumnWidth }}>
                              {visible.map((event) => renderHourlyChip(event, viewMode === "COMPACT"))}
                              {hiddenCount > 0 && (
                                <button
                                  type="button"
                                  className="absence-calendar-hourly-more"
                                  title={items.map((event) => `${getCalendarShortLabel(event, t)} ${formatTime(event.start_at, locale)}–${formatTime(event.end_at, locale)}`).join("\n")}
                                  onClick={() => selectEvent(items[visible.length] || items[0])}
                                >
                                  +{hiddenCount}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}

              {usersForMatrix.length === 0 && (
                <div className="absence-calendar-empty">
                  {t("leaves:absenceCalendar.noUsers", { defaultValue: "No employees to display." })}
                </div>
              )}
            </div>
          </div>
        </section>

        {renderDetailPanel()}
      </main>

      {isEmployeePickerOpen && (
        <div className="absence-calendar-employee-picker-backdrop" role="presentation" onMouseDown={closeEmployeePicker}>
          <section
            className="absence-calendar-employee-picker-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="absence-calendar-employee-picker-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="absence-calendar-employee-picker-header">
              <div>
                <h2 id="absence-calendar-employee-picker-title">
                  {t("leaves:absenceCalendar.employeeSelection.title", { defaultValue: "Select employees" })}
                </h2>
                <p>
                  {t("leaves:absenceCalendar.employeeSelection.description", {
                    defaultValue: "Choose which active employees should be visible in the absence calendar.",
                  })}
                </p>
              </div>
              <button
                type="button"
                className="absence-calendar-employee-picker-close"
                onClick={closeEmployeePicker}
                aria-label={t("common:close", { defaultValue: "Close" })}
              >
                ×
              </button>
            </header>

            <div className="absence-calendar-employee-picker-tools">
              <label>
                <span>{t("common:search", { defaultValue: "Search" })}</span>
                <input
                  type="search"
                  value={employeePickerSearch}
                  onChange={(event) => setEmployeePickerSearch(event.target.value)}
                  placeholder={t("leaves:absenceCalendar.employeeSelection.searchPlaceholder", { defaultValue: "Search employee, username or e-mail..." })}
                  autoFocus
                />
              </label>
              <div className="absence-calendar-employee-picker-actions">
                <button type="button" onClick={selectAllDraftEmployees}>
                  {t("leaves:absenceCalendar.employeeSelection.selectAll", { defaultValue: "Select all" })}
                </button>
                <button type="button" onClick={clearDraftEmployees}>
                  {t("leaves:absenceCalendar.employeeSelection.clearAll", { defaultValue: "Clear all" })}
                </button>
              </div>
            </div>

            <div className="absence-calendar-employee-picker-count">
              {t("leaves:absenceCalendar.employeeSelection.selectedOfTotal", {
                selected: draftSelectedUserIds.length,
                total: allUsers.length,
                defaultValue: `${draftSelectedUserIds.length} of ${allUsers.length}`,
              })}
            </div>

            <div className="absence-calendar-employee-picker-table-wrap">
              <table className="absence-calendar-employee-picker-table">
                <thead>
                  <tr>
                    <th>{t("leaves:absenceCalendar.employeeSelection.selected", { defaultValue: "Selected" })}</th>
                    <th>{t("leaves:absenceCalendar.employee", { defaultValue: "Employee" })}</th>
                    <th>{t("leaves:absenceCalendar.employeeSelection.username", { defaultValue: "Username" })}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEmployeePickerUsers.map((user) => {
                    const userId = Number(user.id);
                    const checked = draftSelectedUserIds.includes(userId);

                    return (
                      <tr key={user.id} className={checked ? "is-selected" : ""}>
                        <td>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleDraftEmployee(userId)}
                            aria-label={normalizeEmployeeName(user)}
                          />
                        </td>
                        <td>
                          <button type="button" className="absence-calendar-employee-picker-name" onClick={() => toggleDraftEmployee(userId)}>
                            {normalizeEmployeeName(user)}
                          </button>
                        </td>
                        <td>{user.username || "—"}</td>
                      </tr>
                    );
                  })}

                  {filteredEmployeePickerUsers.length === 0 && (
                    <tr>
                      <td colSpan="3" className="absence-calendar-employee-picker-empty">
                        {t("leaves:absenceCalendar.employeeSelection.noMatches", { defaultValue: "No active employees match the search." })}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <footer className="absence-calendar-employee-picker-footer">
              <button type="button" onClick={closeEmployeePicker}>
                {t("common:cancel", { defaultValue: "Cancel" })}
              </button>
              <button type="button" className="absence-calendar-employee-picker-apply" onClick={applyEmployeePicker}>
                {t("common:apply", { defaultValue: "Apply" })}
              </button>
            </footer>
          </section>
        </div>
      )}
    </div>
  );
};

export default AbsenceCalendarPage;
